import * as _ from 'lodash'
import {BehaviorSubject} from 'rxjs'
import {ASync} from '../PA.Core/Basic/ASync'
import * as TPMConstants from '../PA.TPM.Core/TPM.Constants'

import {EventBO} from "../PA.TPM.BusinessObjects/EventBO";
import {Subscription} from "rxjs/Subscription";
import {IEventActualsCalculator} from "./PA.TPM.Services.IF";
import {IDateRange, IMessageProvider, IProgressProvider} from "../PA.Core/PA.Core.IF.Basic";
import {MessageProvider} from "../PA.Core/Basic/Messages";
import {ProgressProvider} from "../PA.Core/Basic/Progress";
import {
    IEventChangedEvent, IEventProductGroupChangedEvent, IEventRedistributedSpendChangedEvent,
    IEventSpendChangedEvent
} from "../PA.TPM.Core/PA.TPM.IF.Aspects";
import {MetricParameters, MetricsParameterSet} from "../PA.Metric.Services/PAMetrics";
import {
    WB_CustomerCalendarRow,
    WB_DomainItemRow,
    WB_EventCustomerRow,
    WB_EventFRRow,
    WB_EventOverrideRow,
    WB_EventProductGroupOverrideRow,
    WB_EventProductGroupRow,
    WB_EventProductRow,
    WB_EventRow,
    WB_EventSpendPrdGrpRedistribRow,
    WB_EventSpendPrdGrpRow,
    WB_MetricRow,
    WB_SpendTypeRow
} from "../PA.Core/PAdbSchemaRows";
import {PAMetricServiceClient} from "../PA.Metric.Services/PAMetricServiceClient";
import {
    DetailAttributes,
    IMetricDetails,
    IMetricParameters,
    IMetricParameterSet,
    IMetrics,
    IMetricsSet
} from "../PA.Metric.Services/PA.Metrics.IF";
import {DateUtils} from "../PA.Core/Basic/Dates";
import {
    Spend_Basis_Fixed,
    Spend_Calc_Method_Consumption, Spend_Calc_Method_Shipments, Spend_Calc_Method_TPM_Actual,
    Spend_Calc_Method_TPM_LE, Spend_Redistrib_Basis_Fixed, Spend_Redistrib_Basis_Full,
    Spend_Redistrib_Basis_Pcnt, Spend_Calc_Method_Override, Spend_Calc_Method_Planned
} from "../PA.TPM.Core/TPM.Constants";
import {
    esWB_Customer_Calendar,
    esWB_Domain_Item,
    esWB_Event_Customer,
    esWB_Event_Override,
    esWB_Event_Product,
    esWB_Event_Product_Group,
    esWB_Spend_Type
} from "../PA.Core/PAdbSchema";
import {ExecutionSerializer} from "../PA.Core/Basic/ExecutionSerializer"
import {EventStandardCalculator} from "./EventStandardCalculator";
import {NumberUtils} from '../PA.Core/Basic/Numbers';
import { MetricRowUtils } from "./MetricRowUtils"
import { IwbMetric } from "../PA.Core/PAdbSchemaRowsIF"


// Calculation steps
//
//  Local Event Steps
// E1. Align Dates                                                            CalculateStep_Lcl_alignDates
// E2. Request/ Apply  Actuals Evt/EvtPG/EvtSPG Volumes from Server           CalculateStep_Lcl_loadActualVolumes
// E3. Apply Volume Overrides                                                 CalculateStep_Lcl_applyActualOverrides
// E4. Calc Spend Actual/ Actual Ovrd                                         CalculateStep_Lcl_calcSpend
//
//  Remote Event Steps
// R1. Calc Overlap Dates                                                     CalculateStep_Rdst_calcDates
// R2. Request/ Apply Overlap Columes                                         CalculateStep_Rdst_loadOverlapVolumes
// R3. Calc Redistributed Spend                                               CalculateStep_Rdst_calcSpend
//
//  Common Steps
// C1. Aggregate Spend                                                        CalculateStep_Common_aggregateSpend
// C2. Calc Compound Metrics                                                  CalculateStep_Common_calcCompoundMetric
//
//
// Eventing Step Execution
//
//   On Full Calc Request:                                     ( E1-4, R1-3, C1-2 )
//
//   On Event Execution Bounds Change:                         ( E1-4, C1-2 )
//
//   On Event Volume Override Change:                          ( E3-4, C1-2 )
//
//   On Event Spend Rate/ Type Change (Non Volume Change):     ( E4, C1-2 )
//
//   On Event Spend Date/ Product/ SC Change (Volume Change):  ( E1-4, C1-2 )  (Could optimize with finer steps for Spend Volume)
//
//   On Event Redist Spend (Volume Change):                    ( R1-3, C1-2 )
//
//   On Event Redist Spend (Non Volume Change):                ( R3, C1-2 )
//


// Calc Queuing
//  In order to avoid calc storms during multiple quick updates, add a wait before processing the queue.

const bDebugSerializers = true

interface ICalcStepOptions {
    bUseQueueing?: boolean,
    bUseProgressProvider?: boolean,
    bUseMessageProvider?: boolean,

    bRunCalculateStep_Lcl_alignDates?: boolean,
    bRunCalculateStep_Lcl_loadActualVolumes?: boolean,
    bRunCalculateStep_Lcl_applyActualOverrides?: boolean,
    bRunCalculateStep_Lcl_calcSpend?: boolean,

    bRunCalculateStep_Rdst_calcDates?: boolean,
    bRunCalculateStep_Rdst_loadOverlapVolumes?: boolean,
    bRunCalculateStep_Rdst_calcSpend?: boolean,

    bRunCalculateStep_Common_aggregateSpend?: boolean,
    bRunCalculateStep_Common_calcCompoundMetric?: boolean,

}

const CalcStepDefaults: ICalcStepOptions = {
    bUseQueueing: false,
    bUseProgressProvider: false,
    bUseMessageProvider: false,

    bRunCalculateStep_Lcl_alignDates: false,
    bRunCalculateStep_Lcl_loadActualVolumes: false,
    bRunCalculateStep_Lcl_applyActualOverrides: false,
    bRunCalculateStep_Lcl_calcSpend: false,

    bRunCalculateStep_Rdst_calcDates: false,
    bRunCalculateStep_Rdst_loadOverlapVolumes: false,
    bRunCalculateStep_Rdst_calcSpend: false,

    bRunCalculateStep_Common_aggregateSpend: false,
    bRunCalculateStep_Common_calcCompoundMetric: false

}


export class EventActualsCalculator
    implements
        IEventActualsCalculator
{

    private _enabled: boolean
    private anEventBO: EventBO
    private _CalcProgressProvider: IProgressProvider;
    private _CalcMessageProvider: IMessageProvider;
    private _OnCalculateComplete: BehaviorSubject<IEventActualsCalculator>;

    private _EventChangeSubscription: Subscription
    private _EventPGChangeSubscription: Subscription
    private _EventSPGChangeSubscription: Subscription
    private _EventRSPGChangeSubscription: Subscription
    private _EventExecutionBoundsChanged: Subscription
    private _EventExecutionBoundsChangedCounter: number

    // For Each Entry point, we need a serializer

    private qsEvent_fullCalc: ExecutionSerializer
    private qsEvent_lcl_VolumeCalc: ExecutionSerializer
    private qsEvent_lcl_VolumeOvrdCalc: ExecutionSerializer
    private qsEvent_lcl_SpendCalc: ExecutionSerializer
    private qsEvent_rdst_VolumeCalc: ExecutionSerializer
    private qsEvent_rdst_SpendCalc: ExecutionSerializer
    private qsEvent_cmmn_AggSpend: ExecutionSerializer
    private qsEvent_lcl_OverlapCalc: ExecutionSerializer

    private _customerCalendars: WB_CustomerCalendarRow[]

    constructor(options?: any) {

        this._enabled = true
        this.anEventBO = undefined
        this._CalcMessageProvider = new MessageProvider()
        this._CalcProgressProvider = new ProgressProvider()
        this._OnCalculateComplete = new BehaviorSubject<IEventActualsCalculator>(undefined)

        this._EventChangeSubscription = undefined
        this._EventPGChangeSubscription = undefined
        this._EventSPGChangeSubscription = undefined
        this._EventRSPGChangeSubscription = undefined
        this._EventExecutionBoundsChanged = undefined
        this._EventExecutionBoundsChangedCounter = 0

        this._customerCalendars = []


        this.qsEvent_fullCalc = new ExecutionSerializer(this, this.CalculateStep_Start.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Lcl_alignDates: true,
                bRunCalculateStep_Lcl_loadActualVolumes: true,
                bRunCalculateStep_Lcl_applyActualOverrides: true,
                bRunCalculateStep_Lcl_calcSpend: true,
                bRunCalculateStep_Rdst_calcDates: true,
                bRunCalculateStep_Rdst_loadOverlapVolumes: true,
                bRunCalculateStep_Rdst_calcSpend: true,
                bRunCalculateStep_Common_aggregateSpend: true,
                bRunCalculateStep_Common_calcCompoundMetric: true
            }], 2000, bDebugSerializers)

        this.qsEvent_lcl_VolumeCalc = new ExecutionSerializer(this, this.CalculateStep_Lcl_alignDates.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Lcl_alignDates: true,
                bRunCalculateStep_Lcl_loadActualVolumes: true,
                bRunCalculateStep_Lcl_applyActualOverrides: true // starts next step
            }], 2000, bDebugSerializers)

        this.qsEvent_lcl_VolumeOvrdCalc = new ExecutionSerializer(this, this.CalculateStep_Lcl_applyActualOverrides.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Lcl_applyActualOverrides: true,
                bRunCalculateStep_Lcl_calcSpend: true
            }], 250, bDebugSerializers)

        this.qsEvent_lcl_SpendCalc = new ExecutionSerializer(this, this.CalculateStep_Lcl_calcSpend.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Lcl_calcSpend: true,
                bRunCalculateStep_Common_aggregateSpend: true
            }], 250, bDebugSerializers)

        /*
                this.qsEvent_lcl_OverlapCalc = new ExecutionSerializer(this, this.CalculateStep_Lcl_calcOverlap.bind(this),
                    [{
                        bUseQueueing: true,
                        bUseProgressProvider: false,
                        bUseMessageProvider: false,
                        bRunCalculateStep_Lcl_calcOverlap: true
                    }], 250, bDebugSerializers)
        */
        this.qsEvent_rdst_VolumeCalc = new ExecutionSerializer(this, this.CalculateStep_Rdst_calcDates.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Rdst_calcDates: true,
                bRunCalculateStep_Rdst_loadOverlapVolumes: true,
                bRunCalculateStep_Rdst_calcSpend: true
            }], 1000, bDebugSerializers)

        this.qsEvent_rdst_SpendCalc = new ExecutionSerializer(this, this.CalculateStep_Rdst_calcSpend.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Rdst_calcSpend: true,
                bRunCalculateStep_Common_aggregateSpend: true
            }], 250, bDebugSerializers)


        this.qsEvent_cmmn_AggSpend = new ExecutionSerializer(this, this.CalculateStep_Common_aggregateSpend.bind(this),
            [{
                bUseQueueing: true,
                bUseProgressProvider: false,
                bUseMessageProvider: false,
                bRunCalculateStep_Common_aggregateSpend: true,
                bRunCalculateStep_Common_calcCompoundMetric: true
            }], 50, bDebugSerializers)

    }


    async Register(pEventBO: EventBO): Promise<void> {
        const self = this

        self.UnRegister()

        if (_.isEmpty(pEventBO)) {
            return
        }

        // Watch
        //   Date Change  (Event level, Spend Level)
        //   PG Add
        ///     PG Ovrd Change
        //   SPG Add
        //     SPG Ovrd Change

        self.anEventBO = pEventBO

        self._EventChangeSubscription = self.anEventBO.EventPropertyChanged.subscribe(self.OnEventChange.bind(self))
        self._EventPGChangeSubscription = self.anEventBO.ProductGroupPropertyChanged.subscribe(self.OnEventPGChange.bind(self))
        self._EventSPGChangeSubscription = self.anEventBO.SpendPropertyChanged.subscribe(self.OnEventSPGChange.bind(self))
        self._EventRSPGChangeSubscription = self.anEventBO.RedistributedSpendPropertyChanged.subscribe(self.OnEventRSPGChange.bind(self))
        self._EventExecutionBoundsChanged = self.anEventBO.ExecutionBoundsChanged.subscribe(self.OnExecutionBoundsChange.bind(self))


        self.qsEvent_fullCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_fullCalc'
        self.qsEvent_lcl_VolumeCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_lcl_VolumeCalc'
        self.qsEvent_lcl_VolumeOvrdCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_lcl_VolumeOvrdCalc'
        self.qsEvent_lcl_SpendCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_lcl_SpendCalc'
        self.qsEvent_rdst_VolumeCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_rdst_VolumeCalc'
        self.qsEvent_rdst_SpendCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_rdst_SpendCalc'
        self.qsEvent_cmmn_AggSpend.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_cmmn_AggSpend'
        //self.qsEvent_lcl_OverlapCalc.Name = pEventBO.WB_Event.cif.EVENT_ID + ': qsEvent_lcl_OverlapCalc'

        let wbCustId_Calendar = pEventBO.WB_Event.cif.WB_CUST_ID_PLANNING
        self._customerCalendars = await self.anEventBO.dbContext.findAll(esWB_Customer_Calendar, {
            where: {
                DELETED_IND: {neq: '1'},
                WB_CUST_ID: wbCustId_Calendar
            }
        })
        self._customerCalendars = _.sortBy(self._customerCalendars, ['EffectiveStartDt', 'EffectiveEndDt'])

        // Avoid initial Event Calc
        await self.AlignDates();

        let dctWB_SpendM_IDs: { [id: number]: number } = {}
        let dctBasisIds: { [id: number]: number } = {}

        _.forEach(pEventBO.WB_Event.WB_EventSpend_ProductGroupRows, (aRow) => {
            let wbSpendMId = _.toNumber(aRow.cif.WB_SPENDM_ID)
            if (wbSpendMId > 0)
                dctWB_SpendM_IDs[wbSpendMId] = wbSpendMId
        })

        _.forEach(pEventBO.WB_Event.WB_EventSpend_RedistributionRows, (aRow) => {
            let espg = aRow.WB_EventSpendPrdGrpRow
            let BasisId = _.toNumber(aRow?.cif?.BASIS_ID) || 0
            let wbSpendMId = _.toNumber(espg?.cif?.WB_SPENDM_ID) || 0

            if (BasisId > 0)
                dctBasisIds[BasisId] = BasisId

            if (wbSpendMId > 0)
                dctWB_SpendM_IDs[wbSpendMId] = wbSpendMId

        })

        if (!_.isEmpty(dctBasisIds))
            await pEventBO.dbContext.findUsingCache(esWB_Domain_Item, _.values(dctBasisIds))

        if (!_.isEmpty(dctWB_SpendM_IDs))
            await pEventBO.dbContext.findUsingCache(esWB_Spend_Type, _.values(dctWB_SpendM_IDs))

    }

    UnRegister() {

        if (this._EventExecutionBoundsChanged) {
            this._EventExecutionBoundsChanged.unsubscribe()
            this._EventExecutionBoundsChanged = undefined
        }

        if (this._EventChangeSubscription) {
            this._EventChangeSubscription.unsubscribe()
            this._EventChangeSubscription = undefined
        }

        if (this._EventPGChangeSubscription) {
            this._EventPGChangeSubscription.unsubscribe()
            this._EventPGChangeSubscription = undefined
        }

        if (this._EventSPGChangeSubscription) {
            this._EventSPGChangeSubscription.unsubscribe()
            this._EventSPGChangeSubscription = undefined
        }

        if (this._EventRSPGChangeSubscription) {
            this._EventRSPGChangeSubscription.unsubscribe()
            this._EventRSPGChangeSubscription = undefined
        }


        this.anEventBO = undefined

    }

    async AlignDates(): Promise<void> {

        if (_.isEmpty(this.anEventBO))
            return

        await this.CalculateStep_Lcl_alignDates();
    }

    get Enabled(): boolean {
        return this._enabled
    }

    set Enabled(value) {
        this._enabled = value
    }

    get Event(): EventBO {
        return this.anEventBO
    }

    get CalcProgressProvider(): IProgressProvider {
        return this._CalcProgressProvider
    }

    get CalcMessageProvider(): IMessageProvider {
        return this._CalcMessageProvider
    }

    get OnCalculateComplete(): BehaviorSubject<IEventActualsCalculator> {
        return this._OnCalculateComplete
    }


    // Change Events

    //<editor-fold desc="Change Events">

    private OnExecutionBoundsChange(oEvent: EventBO) {
        if (_.isEmpty(oEvent)) {
            return
        }
        this._EventExecutionBoundsChangedCounter += 1

        // Ignore call on subscribe
        if (this._EventExecutionBoundsChangedCounter > 1) {
            this.queueLclVolumeCalc("BoundsChanged")
        }

    }

    private OnEventChange(oEvent: IEventChangedEvent) {
        if (_.isEmpty(oEvent)) {
            return
        }

        //oEvent.EventBO.WB_Event.WB_EventOverrideRow.cif.ACTUAL_VOL_METHOD_ID
        switch (oEvent.PropertyChanged) {
            case 'WB_EventOverrideRow.ACTUAL_VOL_METHOD_ID':
                this.queueLclVolumeCalc(oEvent.PropertyChanged)
                break;
        }

    }

    private OnEventPGChange(oEvent: IEventProductGroupChangedEvent) {
        if (_.isEmpty(oEvent)) {
            return
        }

        //oEvent.EventBO.WB_Event.WB_Event_ProductGroupRows[0].WB_EventProductGroupOverrideRow.cif.RETURN_QTY_OVRD

        switch (oEvent.PropertyChanged) {
            case 'WB_EventProductGroupOverrideRow.CNSMP_INCR_QTY_OVRD':
            case 'WB_EventProductGroupOverrideRow.CNSMP_BASE_QTY_OVRD':
            case 'WB_EventProductGroupOverrideRow.CNSMP_EDLP_QTY_OVRD':
            case 'WB_EventProductGroupOverrideRow.SHIPMENT_QTY_OVRD':
            case 'WB_EventProductGroupOverrideRow.RETURN_QTY_OVRD':
                this.queueLclVolumeOvrdCalc(oEvent.PropertyChanged)
                break;
        }

    }

    private OnEventSPGChange(oEvent: IEventSpendChangedEvent) {
        if (_.isEmpty(oEvent)) {
            return
        }

        //oEvent.EventBO.WB_Event.WB_EventSpend_ProductGroupRows[0].WB_EventSpendPrdGrpOverrideRow.cif.
        switch (oEvent.PropertyChanged) {
            // Attributes that trigger Full Calc
            //case 'WB_EventSpendPrdGrpOverrideRow.MARGINAL_SPEND_IND':
            case 'WB_EventSpendPrdGrpOverrideRow.INSTOREDT_PARENTLINK_IND':
            case 'WB_EventSpendPrdGrpOverrideRow.INSTORE_START_DT_OVRD':
            case 'WB_EventSpendPrdGrpOverrideRow.INSTORE_END_DT_OVRD':
            case 'WB_EventSpendPrdGrpOverrideRow.SHIPMENTDT_PARENTLINK_IND':
            case 'WB_EventSpendPrdGrpOverrideRow.SHIPMENT_START_DT_OVRD':
            case 'WB_EventSpendPrdGrpOverrideRow.SHIPMENT_END_DT_OVRD':
                this.queueLclVolumeCalc(oEvent.PropertyChanged)
                break

            // Attributes that trigger Spend Calc
            case 'WB_SPENDM_ID':
            case 'RATE_UOM':
            case 'RATE_BASIS_ID':
            case 'RATE_AMT':
            case 'RATE_AMT_PER_UNIT':
            case 'REDEMPTION_PCNT':
            case 'WB_EventSpendPrdGrpOverrideRow.LIABILITY_QTY': // should this be here
            case 'WB_EventSpendPrdGrpOverrideRow.LIABILITY_QTY_OVRD':
            case 'WB_EventSpendPrdGrpOverrideRow.RATE_AMT_OVRD':
            case 'WB_EventSpendPrdGrpOverrideRow.SPEND_CALC_METHOD':
            case 'WB_EventSpendPrdGrpOverrideRow.REDEMPTION_PCNT_OVRD':
                this.queueLclSpendCalc(oEvent.PropertyChanged)
                break

        }

    }

    private OnEventRSPGChange(oEvent: IEventRedistributedSpendChangedEvent) {
        if (_.isEmpty(oEvent)) {
            return
        }

        oEvent.EventBO.WB_Event.WB_EventSpend_RedistributionRows[0].cif.WB_SPENDRL_ID
        switch (oEvent.PropertyChanged) {
            case 'BASIS_RATE_AMT':
            case 'BASIS_ID':
            case 'WB_SPENDRL_ID':
                this.queueRdstSpendCalc(oEvent.PropertyChanged)
                break;

            // TODO:
            case 'Add Overlapping Date Fields with smart queuing':
                this.queueRdstVolumeCalc(oEvent.PropertyChanged)
                break;
        }

    }


    private OnEventRSPGCollectionChange(anEvent: EventBO) {
        this.queueRdstVolumeCalc("OnEventRSPGCollectionChange")
    }



    //</editor-fold>


    Calculate(): Promise<void> {
        //

        return this.CalculateStep_Start({
            bUseQueueing: false,
            bUseProgressProvider: true,
            bUseMessageProvider: true,
            bRunCalculateStep_Lcl_alignDates: true,
            bRunCalculateStep_Lcl_loadActualVolumes: true,
            bRunCalculateStep_Lcl_applyActualOverrides: true,
            bRunCalculateStep_Lcl_calcSpend: true,
            bRunCalculateStep_Rdst_calcDates: true,
            bRunCalculateStep_Rdst_loadOverlapVolumes: true,
            bRunCalculateStep_Rdst_calcSpend: true,
            bRunCalculateStep_Common_aggregateSpend: true,
            bRunCalculateStep_Common_calcCompoundMetric: true
        })

    }

    QueueCalculate(): void {
        //
        this.qsEvent_fullCalc.Run({bSkipDebounce: true})
    }


    //<editor-fold desc="Queue Calls">


    private queueLclVolumeCalc(reason?: string) {
        this.qsEvent_lcl_VolumeCalc.Run({sReason: reason})
    }

    private queueLclVolumeOvrdCalc(reason?: string) {
        this.qsEvent_lcl_VolumeOvrdCalc.Run({sReason: reason})
    }


    private queueLclSpendCalc(reason?: string) {
        this.qsEvent_lcl_SpendCalc.Run({sReason: reason})
    }

    private queueLclOverlapCalc(reason?: string) {
       // this.qsEvent_lcl_OverlapCalc.Run({sReason: reason})
    }

    private queueRdstVolumeCalc(reason?: string) {
        this.qsEvent_rdst_VolumeCalc.Run({sReason: reason})
    }

    private queueRdstSpendCalc(reason?: string) {
        this.qsEvent_rdst_SpendCalc.Run({sReason: reason})
    }


    //</editor-fold>

    //<editor-fold desc="Calc Steps">
    // A1
    private CalculateStep_Start(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        if (opts.bUseQueueing) {
            self.qsEvent_lcl_VolumeCalc.Run({bSkipDebounce: true})
            self.qsEvent_rdst_VolumeCalc.Run({bSkipDebounce: true})
            return ASync.resolve()
        }

        // In Promise mode, we need to run the E1-E4 and R1-R3
        //   then Run C1, C2

        return new Promise<void>((resolve, error) => {

            let tasks: Promise<any>[] = []
            if (opts.bUseProgressProvider) {
                self.CalcProgressProvider.start("Event Calc..")
            }

            tasks.push(self.CalculateStep_Lcl_alignDates(
                {
                    bUseQueueing: false,
                    bUseProgressProvider: opts.bUseProgressProvider,
                    bUseMessageProvider: opts.bUseMessageProvider,
                    bRunCalculateStep_Lcl_alignDates: true,
                    bRunCalculateStep_Lcl_loadActualVolumes: true,
                    bRunCalculateStep_Lcl_applyActualOverrides: true,
                    bRunCalculateStep_Lcl_calcSpend: true
                }
            ))

            tasks.push(self.CalculateStep_Rdst_calcDates(
                {
                    bUseQueueing: false,
                    bUseProgressProvider: opts.bUseProgressProvider,
                    bUseMessageProvider: opts.bUseMessageProvider,
                    bRunCalculateStep_Rdst_calcDates: true,
                    bRunCalculateStep_Rdst_loadOverlapVolumes: true,
                    bRunCalculateStep_Rdst_calcSpend: true
                }
            ))

            Promise.all(tasks)
                .then((ignore) => {
                    return self.CalculateStep_Common_aggregateSpend({
                        bUseQueueing: false,
                        bUseProgressProvider: opts.bUseProgressProvider,
                        bUseMessageProvider: opts.bUseMessageProvider,
                        bRunCalculateStep_Common_aggregateSpend: true,
                        bRunCalculateStep_Common_calcCompoundMetric: true
                    })
                }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })

        })
    }


    // E1
    public CalculateStep_Lcl_alignDates(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {

            EventActualsCalculator.Event_Lcl_setEffectiveDates(self.anEventBO)
                .then((ignore) => {

                    if (opts.bRunCalculateStep_Lcl_loadActualVolumes) {
                        return self.CalculateStep_Lcl_loadActualVolumes(opts)
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })

        })
    }

    // E2
    private CalculateStep_Lcl_loadActualVolumes(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {

            let metricParameterSet = undefined
            let metricSet = undefined

            EventActualsCalculator.Event_createMetricParametersSet(self.anEventBO)
                .then((aMetricParameterSet) => {
                    metricParameterSet = aMetricParameterSet
                    return PAMetricServiceClient.staticCalculateMetricSet(aMetricParameterSet)

                }).then((pMetricSet: IMetricsSet) => {
                metricSet = pMetricSet
                return EventActualsCalculator.Event_applyMetricResultSet(self.anEventBO, pMetricSet)
            }).then((ignore) => {
                if (opts.bRunCalculateStep_Lcl_applyActualOverrides) {
                    if (opts.bUseQueueing) {
                        self.qsEvent_lcl_VolumeOvrdCalc.Run({bSkipDebounce: true})
                        return ASync.resolve()
                    } else {
                        return self.CalculateStep_Lcl_applyActualOverrides(opts)
                    }
                } else {
                    return ASync.resolve()
                }
            }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    // E3
    private CalculateStep_Lcl_applyActualOverrides(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {

            EventActualsCalculator.Event_applyVolumeOverrides(self.anEventBO)
                .then((ignore) => {
                    //
                    if (opts.bRunCalculateStep_Lcl_calcSpend) {
                        if (opts.bUseQueueing) {
                            self.qsEvent_lcl_SpendCalc.Run({bSkipDebounce: true})
                            return ASync.resolve()
                        } else {
                            return self.CalculateStep_Lcl_calcSpend(opts)
                        }
                    } else {
                        return ASync.resolve()
                    }

                }).then((ignore) => {

                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    // E4
    private CalculateStep_Lcl_calcSpend(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            EventActualsCalculator.Event_calculateEventSpend(self.anEventBO)
                .then((ignore) => {
                    if (opts.bRunCalculateStep_Common_aggregateSpend) {
                        if (opts.bUseQueueing) {
                            self.qsEvent_cmmn_AggSpend.Run({bSkipDebounce: true})
                            return ASync.resolve()
                        } else {
                            return self.CalculateStep_Common_aggregateSpend(opts)
                        }
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }


    CalculateStep_Lcl_calcOverlap(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {

            let metricParameterSet = undefined
            let metricSet = undefined

            EventActualsCalculator.Event_createUnionMetricParametersSet(self.anEventBO)
                .then((arrMetricParameter: IMetricParameterSet) => {
                    metricParameterSet = arrMetricParameter
                    return PAMetricServiceClient.staticCalculateMetricUnionDetails(arrMetricParameter, {Attributes: [DetailAttributes.PlanningCustomer, DetailAttributes.Date, DetailAttributes.Product]})
                }).then((pMetricSet: IMetricDetails) => {
                metricSet = pMetricSet
                return EventActualsCalculator.Event_applyUnionMetricResultSet(self.anEventBO, pMetricSet)
            }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    // R1
    private CalculateStep_Rdst_calcDates(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {


            EventActualsCalculator.Event_Rdst_alignDates(self.anEventBO)
                .then((ignore) => {
                    if (opts.bRunCalculateStep_Rdst_loadOverlapVolumes) {
                        return self.CalculateStep_Rdst_loadOverlapVolumes(opts)
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()

            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })

        })
    }

    // R2
    private CalculateStep_Rdst_loadOverlapVolumes(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            let metricParameterSet = undefined
            let metricSet = undefined

            EventActualsCalculator.Event_createOverlapMetricParametersSet(self.anEventBO)
                .then((aMetricParameterSet) => {
                    metricParameterSet = aMetricParameterSet
                    return PAMetricServiceClient.staticCalculateMetricSet(aMetricParameterSet)

                })
                .then((pMetricSet: IMetricsSet) => {
                    metricSet = pMetricSet
                    return EventActualsCalculator.Event_applyOverlapMetricResultSet(self.anEventBO, pMetricSet)
                })
                .then((ignore) => {
                    if (opts.bRunCalculateStep_Rdst_calcSpend) {
                        if (opts.bUseQueueing) {
                            self.qsEvent_rdst_SpendCalc.Run({bSkipDebounce: true})
                            return ASync.resolve()
                        } else {
                            return self.CalculateStep_Rdst_calcSpend(opts)
                        }
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()

            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })

        })
    }

    // R3
    private CalculateStep_Rdst_calcSpend(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            EventActualsCalculator.Event_calculateEventRedistribSpend(self.anEventBO)
                .then((ignore) => {
                    if (opts.bRunCalculateStep_Common_aggregateSpend) {
                        if (opts.bUseQueueing) {
                            self.qsEvent_cmmn_AggSpend.Run({bSkipDebounce: true})
                            return ASync.resolve()
                        } else {
                            return self.CalculateStep_Common_aggregateSpend(opts)
                        }
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })

        })
    }

    // C1
    private CalculateStep_Common_aggregateSpend(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            EventActualsCalculator.Event_aggregateEventSpend(self.anEventBO)
                .then((ignore) => {
                    if (opts.bRunCalculateStep_Common_calcCompoundMetric) {
                        return self.CalculateStep_Common_calcCompoundMetric(opts)
                    } else {
                        return ASync.resolve()
                    }
                }).then((ignore) => {
                resolve()

            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    // C2
    private CalculateStep_Common_calcCompoundMetric(options?: ICalcStepOptions): Promise<void> {
        const self = this
        let opts: ICalcStepOptions = {}
        _.defaults(opts, options || {}, CalcStepDefaults)

        if (_.isEmpty(this.anEventBO)) {
            return ASync.resolve()
        }

        return new Promise<void>((resolve, error) => {
            EventActualsCalculator.Event_calculateEventSpendCompoundMetrics(self.anEventBO)
                .then((ignore) => {
                    return ASync.resolve()
                }).then((ignore) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.finish()
                }
                self.OnCalculateComplete.next(self)
                resolve()
            }).catch((err) => {
                if (opts.bUseProgressProvider) {
                    self.CalcProgressProvider.exception(err)
                }
                error(err)
            })
        })
    }

    //</editor-fold>

    //<editor-fold desc="Utility Functions ">


    //<editor-fold desc="CalculateStep_Lcl_alignDates  Utils">

    static async Event_Lcl_setEffectiveDates(anEvent: EventBO): Promise<void> {

        //
        // Read the Event Planning Customers Calendar
        let evtInStore = DateUtils.coalesce(anEvent.WB_Event.WB_EventOverrideRow.InStore.Range, anEvent.WB_Event.InStore.Range)
        anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range = evtInStore


        let evtShipment = DateUtils.coalesce(anEvent.WB_Event.WB_EventOverrideRow.Shipment.Range, anEvent.WB_Event.Shipment.Range)
        anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range = evtShipment


        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_ProductGroupRows, 'isActive'), (aRow: WB_EventSpendPrdGrpRow) => {
            if (aRow.WB_EventSpendPrdGrpOverrideRow) {
                let spgInStore = DateUtils.coalesce(aRow.WB_EventSpendPrdGrpOverrideRow.InStoreOvrd.Range, {StartDt: aRow.InStoreStartDt, EndDt: aRow.InStoreEndDt})
                aRow.WB_EventSpendPrdGrpOverrideRow.InStoreAligned.Range = spgInStore


                let spgShipment = DateUtils.coalesce(aRow.WB_EventSpendPrdGrpOverrideRow.ShipmentOvrd.Range, {StartDt: aRow.ShipmentStartDt, EndDt: aRow.ShipmentEndDt})
                aRow.WB_EventSpendPrdGrpOverrideRow.ShipmentAligned.Range = spgShipment
            }
        })

    }


    //</editor-fold>

    //<editor-fold desc="CalculateStep_Lcl_loadActualVolumes  Utils">

    static Event_createMetricParametersSet(anEvent: EventBO, bCreateAdjacentParams: boolean = false): Promise<MetricsParameterSet> {
        const self = this
        let returnValue = new MetricsParameterSet()


        // Event/ Event PGs / Event SPGs
        let eventParameters = new MetricParameters()
        eventParameters.MetricParameterId = 'EVT-' + anEvent.WB_Event.cif.WB_EVT_ID
        eventParameters.bCalculateIntersection = false
        eventParameters.VolumeAlignment = _.cloneDeep(anEvent.VolumeAlignment)
        eventParameters.PlanningCustomers.push(anEvent.WB_Event.cif.WB_CUST_ID_PLANNING)
        eventParameters.SourceCustomers = _.map(anEvent.SourceCustomers, (aRow) => {
            return aRow.cif.WB_CUST_ID
        })
        eventParameters.InStoreStartDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.StartDt || anEvent.InStore.StartDt
        eventParameters.InStoreEndDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.EndDt || anEvent.InStore.EndDt
        eventParameters.ShipmentStartDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.StartDt || anEvent.Shipment.StartDt
        eventParameters.ShipmentEndDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.EndDt || anEvent.Shipment.EndDt


        let dctFPsByPG: { [pg_id: number]: number[] } = {}

        function pushPGFP(pg_id: number, fp_id: number) {

            if (!(pg_id in dctFPsByPG)) {
                dctFPsByPG[pg_id] = [fp_id]
            } else {
                dctFPsByPG[pg_id].push(fp_id)
            }
        }

        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, (aRow: WB_EventProductGroupRow) => {
            return aRow.isActive && (!_.isEmpty(aRow.WB_ProductGroupRow))
        }), (aEPGRow: WB_EventProductGroupRow) => {
            // Get Products
            _.forEach(_.filter(aEPGRow.WB_EventProductRows, 'isActive'), (aFPRow) => {
                pushPGFP(0, aFPRow.cif.WB_PROD_ID)
                pushPGFP(aEPGRow.cif.WB_EVTPG_ID, aFPRow.cif.WB_PROD_ID)
            })

            let eventPGParameters = new MetricParameters()
            eventPGParameters.MetricParameterId = 'EVTPG-' + aEPGRow.cif.WB_EVTPG_ID
            eventPGParameters.bCalculateIntersection = false
            eventPGParameters.VolumeAlignment = _.cloneDeep(anEvent.VolumeAlignment)
            eventPGParameters.PlanningCustomers = eventParameters.PlanningCustomers
            eventPGParameters.SourceCustomers = eventParameters.SourceCustomers
            eventPGParameters.InStoreStartDt = eventParameters.InStoreStartDt
            eventPGParameters.InStoreEndDt = eventParameters.InStoreEndDt
            eventPGParameters.ShipmentStartDt = eventParameters.ShipmentStartDt
            eventPGParameters.ShipmentEndDt = eventParameters.ShipmentEndDt
            eventPGParameters.FinishedProducts = dctFPsByPG[aEPGRow.cif.WB_EVTPG_ID] || []
            returnValue[eventPGParameters.MetricParameterId] = eventPGParameters
        })

        eventParameters.FinishedProducts = dctFPsByPG[0] || []
        returnValue[eventParameters.MetricParameterId] = eventParameters


        if (bCreateAdjacentParams) {
            EventActualsCalculator.Event_createAdjacentMetricParameters(returnValue, eventParameters.MetricParameterId)
        }

        dctFPsByPG = {}
        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_ProductGroupRows, (aRow: WB_EventSpendPrdGrpRow) => {
            return aRow.isActive && aRow.WB_ProductGroupRow
        }), (aSPGRow: WB_EventSpendPrdGrpRow) => {
            //
            _.forEach(_.filter(aSPGRow.WB_EventSpendPrdRows, 'isActive'), (aFPRow) => {
                pushPGFP(aSPGRow.cif.WB_EVTSPG_ID, aFPRow.cif.WB_PROD_ID)
            })

            let eventSPGParameters = new MetricParameters()
            eventSPGParameters.MetricParameterId = 'EVTSPG-' + aSPGRow.cif.WB_EVTSPG_ID
            eventSPGParameters.bCalculateIntersection = false
            eventSPGParameters.VolumeAlignment = _.cloneDeep(anEvent.VolumeAlignment)
            eventSPGParameters.PlanningCustomers = eventParameters.PlanningCustomers
            // TODO: Update, Use Tactic Source Customers.. when present
            eventSPGParameters.SourceCustomers = eventParameters.SourceCustomers
            eventSPGParameters.InStoreStartDt = aSPGRow.WB_EventSpendPrdGrpOverrideRow.InStoreAligned.Range.StartDt || eventParameters.InStoreStartDt
            eventSPGParameters.InStoreEndDt = aSPGRow.WB_EventSpendPrdGrpOverrideRow.InStoreAligned.Range.EndDt  || eventParameters.InStoreEndDt
            eventSPGParameters.ShipmentStartDt = aSPGRow.WB_EventSpendPrdGrpOverrideRow.ShipmentAligned.Range.StartDt || eventParameters.ShipmentStartDt
            eventSPGParameters.ShipmentEndDt = aSPGRow.WB_EventSpendPrdGrpOverrideRow.ShipmentAligned.Range.EndDt ||  eventParameters.ShipmentEndDt
            eventSPGParameters.FinishedProducts = dctFPsByPG[aSPGRow.cif.WB_EVTSPG_ID] || []
            returnValue[eventSPGParameters.MetricParameterId] = eventSPGParameters
        })

        return ASync.resolve(returnValue)
    }

    static Event_createAdjacentMetricParameters(aMetricParameterSet: MetricsParameterSet, id: string) {
        let srcParams = aMetricParameterSet[id]
        if (!_.isEmpty(srcParams))
            return

        if (_.isDate(srcParams.InStoreStartDt)) {
            let leftParams = new MetricParameters()
            leftParams.MetricParameterId = id + '-left'
            leftParams.bCalculateIntersection = false
            leftParams.PlanningCustomers = srcParams.PlanningCustomers
            leftParams.SourceCustomers = srcParams.SourceCustomers
            leftParams.FinishedProducts = srcParams.FinishedProducts
            leftParams.InStoreEndDt = DateUtils.addDays(srcParams.InStoreStartDt, -1)
            leftParams.InStoreStartDt = DateUtils.addDays(leftParams.InStoreEndDt, -6)
            aMetricParameterSet[leftParams.MetricParameterId] = leftParams
        }

        if (_.isDate(srcParams.InStoreEndDt)) {
            let rightParams = new MetricParameters()
            rightParams.MetricParameterId = id + '-right'
            rightParams.bCalculateIntersection = false
            rightParams.PlanningCustomers = srcParams.PlanningCustomers
            rightParams.SourceCustomers = srcParams.SourceCustomers
            rightParams.FinishedProducts = srcParams.FinishedProducts
            rightParams.InStoreStartDt = DateUtils.addDays(srcParams.InStoreEndDt, 1)
            rightParams.InStoreEndDt = DateUtils.addDays(rightParams.InStoreStartDt, 6)
            aMetricParameterSet[rightParams.MetricParameterId] = rightParams
        }


    }

    static Event_applyMetricResultSet(anEvent: EventBO, aMetricsSet: IMetricsSet): Promise<void> {

        // Event Level Metrics
        let evtKey = 'EVT-' + anEvent.WB_Event.cif.WB_EVT_ID
        let modifiedDTTS = DateUtils.convertToSAPBWTs(new Date())
        if (evtKey in aMetricsSet) {
            let evtActMetrics = anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_Actual_Pending
            let evtActOvrdMetrics = anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_ActualOvrd_Pending

            if (evtActMetrics) {
                MetricRowUtils.applyVolume(evtActMetrics, aMetricsSet[evtKey])
                MetricRowUtils.applyAdjVolume(evtActMetrics, aMetricsSet[evtKey + '-left'], aMetricsSet[evtKey + '-right'])
                evtActMetrics.MODIFIED_DTTS = modifiedDTTS
            }

            if (evtActOvrdMetrics) {
                MetricRowUtils.applyVolume(evtActOvrdMetrics, aMetricsSet[evtKey])
                MetricRowUtils.applyAdjVolume(evtActOvrdMetrics, aMetricsSet[evtKey + '-left'], aMetricsSet[evtKey + '-right'])
                evtActOvrdMetrics.cif.MODIFIED_DTTS = modifiedDTTS
            }
        }

        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, 'isActive'), (aEPGRow: WB_EventProductGroupRow) => {
            let evtpgKey = 'EVTPG-' + aEPGRow.cif.WB_EVTPG_ID
            let modifiedDTTS = DateUtils.convertToSAPBWTs(new Date())

            if (evtpgKey in aMetricsSet) {
                let evtpgActMetrics = <IwbMetric> aEPGRow.WB_EventProductGroupOverrideRow.WB_MetricRow_Actual_Pending
                let evtpgActOvrdMetrics = <IwbMetric> aEPGRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending

                if (evtpgActMetrics) {
                    MetricRowUtils.applyVolume(evtpgActMetrics, aMetricsSet[evtpgKey])
                    MetricRowUtils.applyAdjVolume(evtpgActMetrics, aMetricsSet[evtpgKey + '-left'], aMetricsSet[evtpgKey + '-right'])
                    evtpgActMetrics.MODIFIED_DTTS = modifiedDTTS
                }

                if (evtpgActOvrdMetrics) {
                    MetricRowUtils.applyVolume(evtpgActOvrdMetrics, aMetricsSet[evtpgKey])
                    MetricRowUtils.applyAdjVolume(evtpgActOvrdMetrics, aMetricsSet[evtpgKey + '-left'], aMetricsSet[evtpgKey + '-right'])
                    evtpgActOvrdMetrics.MODIFIED_DTTS = modifiedDTTS
                }
            }
        })

        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_ProductGroupRows, 'isActive'), (aESPGRow: WB_EventSpendPrdGrpRow) => {
            let evtspgKey = 'EVTSPG-' + aESPGRow.cif.WB_EVTSPG_ID
            if (evtspgKey in aMetricsSet) {
                //
                let evtspgActMetrics = aESPGRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_Actual_Pending
                let evtspgActOvrdMetrics = aESPGRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_ActualOvrd_Pending

                if (evtspgActMetrics) {
                    MetricRowUtils.applyVolume(evtspgActMetrics, aMetricsSet[evtspgKey])
                    MetricRowUtils.applyAdjVolume(evtspgActMetrics, undefined, undefined)
                    evtspgActMetrics.MODIFIED_DTTS = modifiedDTTS
                }

                if (evtspgActOvrdMetrics) {
                    MetricRowUtils.applyVolume(evtspgActOvrdMetrics, aMetricsSet[evtspgKey])
                    MetricRowUtils.applyAdjVolume(evtspgActOvrdMetrics, undefined, undefined)
                    evtspgActOvrdMetrics.cif.MODIFIED_DTTS = modifiedDTTS
                }
            }

        })

        return ASync.resolve()
    }


    //</editor-fold>

    //<editor-fold desc="CalculateStep_Lcl_applyActualOverrides  Utils">

    static async Event_applyVolumeOverrides(anEvent: EventBO): Promise<void> {

        let bHasConsumptionOverride: boolean = false
        let bHasConsumptionBaseOverride: boolean = false
        let bHasConsumptionEDLPOverride: boolean = false
        let bHasConsumptionIncrOverride: boolean = false

        let bHasShipmentOverride: boolean = false
        let bHasReturnOverride: boolean = false

        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, 'isActive'), (aRow: WB_EventProductGroupRow) => {

            EventActualsCalculator.MetricRow_applyEventPGVolumeOverrides(aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_Actual_Pending,
                aRow.WB_EventProductGroupOverrideRow,
                aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending)

            //
            if ((aRow.WB_EventProductGroupOverrideRow.cif.CNSMP_BASE_QTY_OVRD || 0) > 0) {
                bHasConsumptionBaseOverride = true
            }

            if ((aRow.WB_EventProductGroupOverrideRow.cif.CNSMP_EDLP_QTY_OVRD || 0) > 0) {
                bHasConsumptionEDLPOverride = true
            }

            if ((aRow.WB_EventProductGroupOverrideRow.cif.CNSMP_INCR_QTY_OVRD || 0) > 0) {
                bHasConsumptionIncrOverride = true
            }

            if ((aRow.WB_EventProductGroupOverrideRow.cif.SHIPMENT_QTY_OVRD || 0) > 0) {
                bHasShipmentOverride = true
            }

            if ((aRow.WB_EventProductGroupOverrideRow.cif.RETURN_QTY_OVRD || 0) > 0) {
                bHasReturnOverride = true
            }
        })

        bHasConsumptionOverride = bHasConsumptionBaseOverride || bHasConsumptionEDLPOverride || bHasConsumptionIncrOverride

        let tgt = <IwbMetric> anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_ActualOvrd_Pending

        if (bHasConsumptionOverride) {
            tgt.CNSMP_BASE_UNITS = 0
            tgt.CNSMP_BASE_WT = 0
            tgt.CNSMP_BASE_STAT = 0
            tgt.CNSMP_BASE_LISTSLS = 0
            tgt.CNSMP_BASE_NETLISTSLS = 0
            tgt.CNSMP_BASE_RTLSLS = 0
            tgt.CNSMP_BASE_PRFTCNTRB = 0

            tgt.CNSMP_EDLP_UNITS = 0
            tgt.CNSMP_EDLP_WT = 0
            tgt.CNSMP_EDLP_STAT = 0
            tgt.CNSMP_EDLP_LISTSLS = 0
            tgt.CNSMP_EDLP_NETLISTSLS = 0
            tgt.CNSMP_EDLP_RTLSLS = 0
            tgt.CNSMP_EDLP_PRFTCNTRB = 0

            tgt.CNSMP_INCR_UNITS = 0
            tgt.CNSMP_INCR_WT = 0
            tgt.CNSMP_INCR_STAT = 0
            tgt.CNSMP_INCR_LISTSLS = 0
            tgt.CNSMP_INCR_NETLISTSLS = 0
            tgt.CNSMP_INCR_RTLSLS = 0
            tgt.CNSMP_INCR_PRFTCNTRB = 0

            tgt.CNSMP_TOTAL_UNITS = 0
            tgt.CNSMP_TOTAL_WT = 0
            tgt.CNSMP_TOTAL_STAT = 0
            tgt.CNSMP_TOTAL_LISTSLS = 0
            tgt.CNSMP_TOTAL_NETLISTSLS = 0
            tgt.CNSMP_TOTAL_RTLSLS = 0
            tgt.CNSMP_TOTAL_PRFTCNTRB = 0
        }

        if (bHasShipmentOverride) {
            tgt.SHIPMENT_UNITS = 0
            tgt.SHIPMENT_WT = 0
            tgt.SHIPMENT_STAT = 0
            tgt.SHIPMENT_LISTSLS = 0
            tgt.SHIPMENT_NETLISTSLS = 0
            tgt.SHIPMENT_PRFTCNTRB = 0
        }

        if (bHasReturnOverride) {
            tgt.RETURN_UNITS = 0
            tgt.RETURN_WT = 0
            tgt.RETURN_STAT = 0
            tgt.RETURN_LISTSLS = 0
            tgt.RETURN_NETLISTSLS = 0
            tgt.RETURN_PRFTCNTRB = 0
        }

        // Aggregate
        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, 'isActive'), (aRow: WB_EventProductGroupRow) => {
            let src = <IwbMetric> aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending
            //
            //
            if (bHasConsumptionOverride) {
                tgt.CNSMP_BASE_UNITS += src.CNSMP_BASE_UNITS
                tgt.CNSMP_BASE_WT += src.CNSMP_BASE_WT
                tgt.CNSMP_BASE_STAT += src.CNSMP_BASE_STAT
                tgt.CNSMP_BASE_LISTSLS += src.CNSMP_BASE_LISTSLS
                tgt.CNSMP_BASE_NETLISTSLS += src.CNSMP_BASE_NETLISTSLS
                tgt.CNSMP_BASE_RTLSLS += src.CNSMP_BASE_RTLSLS
                tgt.CNSMP_BASE_PRFTCNTRB += src.CNSMP_BASE_PRFTCNTRB

                tgt.CNSMP_EDLP_UNITS += src.CNSMP_EDLP_UNITS
                tgt.CNSMP_EDLP_WT += src.CNSMP_EDLP_WT
                tgt.CNSMP_EDLP_STAT += src.CNSMP_EDLP_STAT
                tgt.CNSMP_EDLP_LISTSLS += src.CNSMP_EDLP_LISTSLS
                tgt.CNSMP_EDLP_NETLISTSLS += src.CNSMP_EDLP_NETLISTSLS
                tgt.CNSMP_EDLP_RTLSLS += src.CNSMP_EDLP_RTLSLS
                tgt.CNSMP_EDLP_PRFTCNTRB += src.CNSMP_EDLP_PRFTCNTRB

                tgt.CNSMP_INCR_UNITS += src.CNSMP_INCR_UNITS
                tgt.CNSMP_INCR_WT += src.CNSMP_INCR_WT
                tgt.CNSMP_INCR_STAT += src.CNSMP_INCR_STAT
                tgt.CNSMP_INCR_LISTSLS += src.CNSMP_INCR_LISTSLS
                tgt.CNSMP_INCR_NETLISTSLS += src.CNSMP_INCR_NETLISTSLS
                tgt.CNSMP_INCR_RTLSLS += src.CNSMP_INCR_RTLSLS
                tgt.CNSMP_INCR_PRFTCNTRB += src.CNSMP_INCR_PRFTCNTRB

                tgt.CNSMP_TOTAL_UNITS += src.CNSMP_TOTAL_UNITS
                tgt.CNSMP_TOTAL_WT += src.CNSMP_TOTAL_WT
                tgt.CNSMP_TOTAL_STAT += src.CNSMP_TOTAL_STAT
                tgt.CNSMP_TOTAL_LISTSLS += src.CNSMP_TOTAL_LISTSLS
                tgt.CNSMP_TOTAL_NETLISTSLS += src.CNSMP_TOTAL_NETLISTSLS
                tgt.CNSMP_TOTAL_RTLSLS += src.CNSMP_TOTAL_RTLSLS
                tgt.CNSMP_TOTAL_PRFTCNTRB += src.CNSMP_TOTAL_PRFTCNTRB
            }

            if (bHasShipmentOverride) {
                tgt.SHIPMENT_UNITS += src.SHIPMENT_UNITS
                tgt.SHIPMENT_WT += src.SHIPMENT_WT
                tgt.SHIPMENT_STAT += src.SHIPMENT_STAT
                tgt.SHIPMENT_LISTSLS += src.SHIPMENT_LISTSLS
                tgt.SHIPMENT_NETLISTSLS += src.SHIPMENT_NETLISTSLS
                tgt.SHIPMENT_PRFTCNTRB += src.SHIPMENT_PRFTCNTRB
            }

            if (bHasReturnOverride) {
                tgt.RETURN_UNITS += src.RETURN_UNITS
                tgt.RETURN_WT += src.RETURN_WT
                tgt.RETURN_STAT += src.RETURN_STAT
                tgt.RETURN_LISTSLS += src.RETURN_LISTSLS
                tgt.RETURN_NETLISTSLS += src.RETURN_NETLISTSLS
                tgt.RETURN_PRFTCNTRB += src.RETURN_PRFTCNTRB
            }

        })

    }

    static MetricRow_applyEventPGVolumeOverrides(src: IwbMetric, ovrd: WB_EventProductGroupOverrideRow, tgt: IwbMetric): void {

        let bUseOverrideMethodTotal = true
        let bUseOverrideMethodScale = false

        let bHasConsumptionOverride = false;
        let OvrdFactor = 0;
        let UOM_Factor_Units = 0;
        let UOM_Factor_Wt = 0;
        let UOM_Factor_Stat = 0;
        let UOM_Factor_ListSls = 0;
        let UOM_Factor_NetListSls = 0
        let UOM_Factor_RetailSls = 0;
        let UOM_Factor_Profit_Contrib = 0;

        if (bUseOverrideMethodTotal) {
            UOM_Factor_Units = 1

            if ((src.CNSMP_TOTAL_UNITS || 0) > 0) {
                UOM_Factor_Wt = (src.CNSMP_TOTAL_WT || 0) / (src.CNSMP_TOTAL_UNITS || 0)
                UOM_Factor_Stat = (src.CNSMP_TOTAL_STAT || 0) / (src.CNSMP_TOTAL_UNITS || 0)
                UOM_Factor_ListSls = (src.CNSMP_TOTAL_LISTSLS || 0) / (src.CNSMP_TOTAL_UNITS || 0)
                UOM_Factor_NetListSls = (src.CNSMP_TOTAL_NETLISTSLS || 0) / (src.CNSMP_TOTAL_UNITS || 0)
                UOM_Factor_RetailSls = (src.CNSMP_TOTAL_RTLSLS || 0) / (src.CNSMP_TOTAL_UNITS || 0)
                UOM_Factor_Profit_Contrib = (src.CNSMP_TOTAL_PRFTCNTRB || 0) / (src.CNSMP_TOTAL_UNITS || 0)
            }
        }

        // Reset
        tgt.CNSMP_BASE_UNITS = src.CNSMP_BASE_UNITS
        tgt.CNSMP_BASE_WT = src.CNSMP_BASE_WT
        tgt.CNSMP_BASE_STAT = src.CNSMP_BASE_STAT
        tgt.CNSMP_BASE_LISTSLS = src.CNSMP_BASE_LISTSLS
        tgt.CNSMP_BASE_NETLISTSLS = src.CNSMP_BASE_NETLISTSLS
        tgt.CNSMP_BASE_RTLSLS = src.CNSMP_BASE_RTLSLS
        tgt.CNSMP_BASE_PRFTCNTRB = src.CNSMP_BASE_PRFTCNTRB
        tgt.CNSMP_EDLP_UNITS = src.CNSMP_EDLP_UNITS
        tgt.CNSMP_EDLP_WT = src.CNSMP_EDLP_WT
        tgt.CNSMP_EDLP_STAT = src.CNSMP_EDLP_STAT
        tgt.CNSMP_EDLP_LISTSLS = src.CNSMP_EDLP_LISTSLS
        tgt.CNSMP_EDLP_NETLISTSLS = src.CNSMP_EDLP_NETLISTSLS
        tgt.CNSMP_EDLP_RTLSLS = src.CNSMP_EDLP_RTLSLS
        tgt.CNSMP_EDLP_PRFTCNTRB = src.CNSMP_EDLP_PRFTCNTRB
        tgt.CNSMP_INCR_UNITS = src.CNSMP_INCR_UNITS
        tgt.CNSMP_INCR_WT = src.CNSMP_INCR_WT
        tgt.CNSMP_INCR_STAT = src.CNSMP_INCR_STAT
        tgt.CNSMP_INCR_LISTSLS = src.CNSMP_INCR_LISTSLS
        tgt.CNSMP_INCR_NETLISTSLS = src.CNSMP_INCR_NETLISTSLS
        tgt.CNSMP_INCR_RTLSLS = src.CNSMP_INCR_RTLSLS
        tgt.CNSMP_INCR_PRFTCNTRB = src.CNSMP_INCR_PRFTCNTRB
        tgt.CNSMP_TOTAL_UNITS = src.CNSMP_TOTAL_UNITS
        tgt.CNSMP_TOTAL_WT = src.CNSMP_TOTAL_WT
        tgt.CNSMP_TOTAL_STAT = src.CNSMP_TOTAL_STAT
        tgt.CNSMP_TOTAL_LISTSLS = src.CNSMP_TOTAL_LISTSLS
        tgt.CNSMP_TOTAL_NETLISTSLS = src.CNSMP_TOTAL_NETLISTSLS
        tgt.CNSMP_TOTAL_RTLSLS = src.CNSMP_TOTAL_RTLSLS
        tgt.CNSMP_TOTAL_PRFTCNTRB = src.CNSMP_TOTAL_PRFTCNTRB


        //
        // Apply Base Override
        if ((ovrd.cif.CNSMP_BASE_QTY_OVRD || 0) > 0) {

            bHasConsumptionOverride = true

            if (bUseOverrideMethodTotal) {
                // Apply Base Override UOM Conversion
                tgt.CNSMP_BASE_UNITS = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_Units;
                tgt.CNSMP_BASE_WT = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_Wt;
                tgt.CNSMP_BASE_STAT = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_Stat;
                tgt.CNSMP_BASE_LISTSLS = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_ListSls;
                tgt.CNSMP_BASE_NETLISTSLS = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_NetListSls;
                tgt.CNSMP_BASE_RTLSLS = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_RetailSls;
                tgt.CNSMP_BASE_PRFTCNTRB = ovrd.cif.CNSMP_BASE_QTY_OVRD * UOM_Factor_Profit_Contrib;
            }

            if (bUseOverrideMethodScale && (tgt.CNSMP_BASE_UNITS > 0)) {
                OvrdFactor = (ovrd.cif.CNSMP_BASE_QTY_OVRD || 0) / tgt.CNSMP_BASE_UNITS
                tgt.CNSMP_BASE_UNITS = src.CNSMP_BASE_UNITS * OvrdFactor;
                tgt.CNSMP_BASE_WT = src.CNSMP_BASE_WT * OvrdFactor;
                tgt.CNSMP_BASE_STAT = src.CNSMP_BASE_STAT * OvrdFactor;
                tgt.CNSMP_BASE_LISTSLS = src.CNSMP_BASE_LISTSLS * OvrdFactor;
                tgt.CNSMP_BASE_NETLISTSLS = src.CNSMP_BASE_NETLISTSLS * OvrdFactor;
                tgt.CNSMP_BASE_RTLSLS = src.CNSMP_BASE_RTLSLS * OvrdFactor;
                tgt.CNSMP_BASE_PRFTCNTRB = src.CNSMP_BASE_PRFTCNTRB * OvrdFactor;
            }

        }

        //
        // Apply EDLP Override
        if ((ovrd.cif.CNSMP_EDLP_QTY_OVRD || 0) > 0) {

            bHasConsumptionOverride = true

            if (bUseOverrideMethodTotal) {
                // Apply Base Override UOM Conversion
                tgt.CNSMP_EDLP_UNITS = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_Units;
                tgt.CNSMP_EDLP_WT = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_Wt;
                tgt.CNSMP_EDLP_STAT = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_Stat;
                tgt.CNSMP_EDLP_LISTSLS = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_ListSls;
                tgt.CNSMP_EDLP_NETLISTSLS = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_NetListSls;
                tgt.CNSMP_EDLP_RTLSLS = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_RetailSls;
                tgt.CNSMP_EDLP_PRFTCNTRB = ovrd.cif.CNSMP_EDLP_QTY_OVRD * UOM_Factor_Profit_Contrib;
            }

            if (bUseOverrideMethodScale && (tgt.CNSMP_EDLP_UNITS > 0)) {
                OvrdFactor = (ovrd.cif.CNSMP_EDLP_QTY_OVRD || 0) / tgt.CNSMP_EDLP_UNITS
                tgt.CNSMP_EDLP_UNITS = src.CNSMP_EDLP_UNITS * OvrdFactor;
                tgt.CNSMP_EDLP_WT = src.CNSMP_EDLP_WT * OvrdFactor;
                tgt.CNSMP_EDLP_STAT = src.CNSMP_EDLP_STAT * OvrdFactor;
                tgt.CNSMP_EDLP_LISTSLS = src.CNSMP_EDLP_LISTSLS * OvrdFactor;
                tgt.CNSMP_EDLP_NETLISTSLS = src.CNSMP_EDLP_NETLISTSLS * OvrdFactor;
                tgt.CNSMP_EDLP_RTLSLS = src.CNSMP_EDLP_RTLSLS * OvrdFactor;
                tgt.CNSMP_EDLP_PRFTCNTRB = src.CNSMP_EDLP_PRFTCNTRB * OvrdFactor;
            }

        }


        //
        // Apply Incr Override
        if ((ovrd.cif.CNSMP_INCR_QTY_OVRD || 0) > 0) {

            bHasConsumptionOverride = true

            if (bUseOverrideMethodTotal) {
                // Apply Base Override UOM Conversion
                tgt.CNSMP_INCR_UNITS = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_Units;
                tgt.CNSMP_INCR_WT = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_Wt;
                tgt.CNSMP_INCR_STAT = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_Stat;
                tgt.CNSMP_INCR_LISTSLS = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_ListSls;
                tgt.CNSMP_INCR_NETLISTSLS = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_NetListSls;
                tgt.CNSMP_INCR_RTLSLS = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_RetailSls;
                tgt.CNSMP_INCR_PRFTCNTRB = ovrd.cif.CNSMP_INCR_QTY_OVRD * UOM_Factor_Profit_Contrib;
            }

            if (bUseOverrideMethodScale && (tgt.CNSMP_INCR_UNITS > 0)) {
                OvrdFactor = (ovrd.cif.CNSMP_INCR_QTY_OVRD || 0) / tgt.CNSMP_INCR_UNITS
                tgt.CNSMP_INCR_UNITS = src.CNSMP_INCR_UNITS * OvrdFactor;
                tgt.CNSMP_INCR_WT = src.CNSMP_INCR_WT * OvrdFactor;
                tgt.CNSMP_INCR_STAT = src.CNSMP_INCR_STAT * OvrdFactor;
                tgt.CNSMP_INCR_LISTSLS = src.CNSMP_INCR_LISTSLS * OvrdFactor;
                tgt.CNSMP_INCR_NETLISTSLS = src.CNSMP_INCR_NETLISTSLS * OvrdFactor;
                tgt.CNSMP_INCR_RTLSLS = src.CNSMP_INCR_RTLSLS * OvrdFactor;
                tgt.CNSMP_INCR_PRFTCNTRB = src.CNSMP_INCR_PRFTCNTRB * OvrdFactor;
            }

        }

        // Update Total
        if (bHasConsumptionOverride) {
            tgt.CNSMP_TOTAL_UNITS = tgt.CNSMP_INCR_UNITS + tgt.CNSMP_EDLP_UNITS + tgt.CNSMP_BASE_UNITS
            tgt.CNSMP_TOTAL_WT = tgt.CNSMP_INCR_WT + tgt.CNSMP_EDLP_WT + tgt.CNSMP_BASE_WT
            tgt.CNSMP_TOTAL_STAT = tgt.CNSMP_INCR_STAT + tgt.CNSMP_EDLP_STAT + tgt.CNSMP_BASE_STAT
            tgt.CNSMP_TOTAL_LISTSLS = tgt.CNSMP_INCR_LISTSLS + tgt.CNSMP_EDLP_LISTSLS + tgt.CNSMP_BASE_LISTSLS
            tgt.CNSMP_TOTAL_NETLISTSLS = tgt.CNSMP_INCR_NETLISTSLS + tgt.CNSMP_EDLP_NETLISTSLS + tgt.CNSMP_BASE_NETLISTSLS
            tgt.CNSMP_TOTAL_RTLSLS = tgt.CNSMP_INCR_RTLSLS + tgt.CNSMP_EDLP_RTLSLS + tgt.CNSMP_BASE_RTLSLS
            tgt.CNSMP_TOTAL_PRFTCNTRB = tgt.CNSMP_INCR_PRFTCNTRB + tgt.CNSMP_EDLP_PRFTCNTRB + tgt.CNSMP_BASE_PRFTCNTRB
        }


        // Do Shipment
        UOM_Factor_Units = 0
        UOM_Factor_Wt = 0
        UOM_Factor_Stat = 0
        UOM_Factor_ListSls = 0
        UOM_Factor_NetListSls = 0
        UOM_Factor_RetailSls = 0
        UOM_Factor_Profit_Contrib = 0

        if (bUseOverrideMethodTotal) {
            UOM_Factor_Units = 1

            if ((src.SHIPMENT_UNITS || 0) > 0) {
                UOM_Factor_Wt = (src.SHIPMENT_WT || 0) / (src.SHIPMENT_UNITS || 0)
                UOM_Factor_Stat = (src.SHIPMENT_STAT || 0) / (src.SHIPMENT_UNITS || 0)
                UOM_Factor_ListSls = (src.SHIPMENT_LISTSLS || 0) / (src.SHIPMENT_UNITS || 0)
                UOM_Factor_NetListSls = (src.SHIPMENT_NETLISTSLS || 0) / (src.SHIPMENT_UNITS || 0)
                UOM_Factor_Profit_Contrib = (src.SHIPMENT_PRFTCNTRB || 0) / (src.SHIPMENT_UNITS || 0)
            }
        }


        if ((ovrd.cif.SHIPMENT_QTY_OVRD || 0) > 0) {

            if (bUseOverrideMethodTotal) {
                // Apply Base Override UOM Conversion
                tgt.SHIPMENT_UNITS = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_Units;
                tgt.SHIPMENT_WT = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_Wt;
                tgt.SHIPMENT_STAT = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_Stat;
                tgt.SHIPMENT_LISTSLS = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_ListSls;
                tgt.SHIPMENT_NETLISTSLS = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_NetListSls;
                tgt.SHIPMENT_PRFTCNTRB = ovrd.cif.SHIPMENT_QTY_OVRD * UOM_Factor_Profit_Contrib;
            }

            if (bUseOverrideMethodScale && (tgt.SHIPMENT_UNITS > 0)) {
                OvrdFactor = (ovrd.cif.SHIPMENT_QTY_OVRD || 0) / tgt.SHIPMENT_UNITS
                tgt.SHIPMENT_UNITS = src.SHIPMENT_UNITS * OvrdFactor;
                tgt.SHIPMENT_WT = src.SHIPMENT_WT * OvrdFactor;
                tgt.SHIPMENT_STAT = src.SHIPMENT_STAT * OvrdFactor;
                tgt.SHIPMENT_LISTSLS = src.SHIPMENT_LISTSLS * OvrdFactor;
                tgt.SHIPMENT_NETLISTSLS = src.SHIPMENT_NETLISTSLS * OvrdFactor;
                tgt.SHIPMENT_PRFTCNTRB = src.SHIPMENT_PRFTCNTRB * OvrdFactor;
            }

        }

        if ((ovrd.cif.RETURN_QTY_OVRD || 0) > 0) {

            if (bUseOverrideMethodTotal) {
                // Apply Base Override UOM Conversion
                tgt.RETURN_UNITS = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_Units;
                tgt.RETURN_WT = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_Wt;
                tgt.RETURN_STAT = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_Stat;
                tgt.RETURN_LISTSLS = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_ListSls;
                tgt.RETURN_NETLISTSLS = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_NetListSls;
                tgt.RETURN_PRFTCNTRB = ovrd.cif.RETURN_QTY_OVRD * UOM_Factor_Profit_Contrib;
            }

            if (bUseOverrideMethodScale && (tgt.SHIPMENT_UNITS > 0)) {
                OvrdFactor = (ovrd.cif.SHIPMENT_QTY_OVRD || 0) / tgt.SHIPMENT_UNITS
                tgt.RETURN_UNITS = src.RETURN_UNITS * OvrdFactor;
                tgt.RETURN_WT = src.RETURN_WT * OvrdFactor;
                tgt.RETURN_STAT = src.RETURN_STAT * OvrdFactor;
                tgt.RETURN_LISTSLS = src.RETURN_LISTSLS * OvrdFactor;
                tgt.RETURN_NETLISTSLS = src.RETURN_NETLISTSLS * OvrdFactor;
                tgt.RETURN_PRFTCNTRB = src.RETURN_PRFTCNTRB * OvrdFactor;
            }

        }


    }

    //</editor-fold>

    //<editor-fold desc="CalculateStep_Lcl_calcSpend  Utils">

    static Event_calculateEventSpend(anEvent: EventBO): Promise<void> {

        return new Promise<void>((resolve, error) => {

            _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_ProductGroupRows, 'isActive'), (anESPGRow: WB_EventSpendPrdGrpRow) => {
                // Update Unit Rate
                EventStandardCalculator.EventSPG_calculateRatePerUnit(anEvent, anESPGRow)

                // Calculate Actual
                EventActualsCalculator.EventSPG_calculate(anEvent, anESPGRow, anESPGRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_Actual_Pending, false)

                //
                // Calculate Actual Ovrd
                EventActualsCalculator.EventSPG_calculate(anEvent, anESPGRow, anESPGRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_ActualOvrd_Pending, true)

            })

            resolve()
        })
    }

    static EventSPG_calculate(evt: EventBO, espg: WB_EventSpendPrdGrpRow, drMetrics: IwbMetric, bConsiderOverrides: boolean = false): void {

        // Reset Spend
        EventActualsCalculator.MetricRow_resetSpend(drMetrics)
        let ovrd = espg.WB_EventSpendPrdGrpOverrideRow

        let SpendAmount = 0
        let CnsSpendAmount = 0
        let ShpSpendAmount = 0
        let OvrdSpendAmount = 0

        let bHasRateOvrd = (!espg.bManualInd) && (bConsiderOverrides && ((ovrd.cif.RATE_AMT_OVRD || 0) > 0))

        function getRate(): number {
            if (espg.bManualInd)
                return espg.cif.RATE_AMT

            if (bConsiderOverrides && ((ovrd.cif.RATE_AMT_OVRD || 0) > 0)) {
                return ovrd.cif.RATE_AMT_OVRD
            }

            return espg.cif.RATE_AMT
        }

        function getUnitRate(): number {
            let aRate = getRate()
            if (espg.WB_ProductGroupRow.cif.CASE_PACK_SIZE > 0) {
                return NumberUtils.round(aRate / espg.WB_ProductGroupRow.cif.CASE_PACK_SIZE, 4)
            } else {
                return NumberUtils.round(espg.cif.RATE_AMT_PER_UNIT || 0, 4)
            }
        }

        let bVariable = !_.includes(TPMConstants.Spend_Basis_Fixed, espg.cif.RATE_BASIS_ID)
        let aRate: number = getRate()
        let aUnitRate: number = getUnitRate()

        switch (true) {
            case _.includes(TPMConstants.Spend_Basis_Fixed, espg.cif.RATE_BASIS_ID):
                SpendAmount = aRate
                CnsSpendAmount = 0
                ShpSpendAmount = 0
                OvrdSpendAmount = (bHasRateOvrd || espg.bManualInd) ? aRate : 0
                break;

            case _.includes(TPMConstants.Spend_Basis_Consumed, espg.cif.RATE_BASIS_ID):
                switch (true) {

                    case _.includes(TPMConstants.Spend_UOM_Units, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_UNITS || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_UNITS || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Case, espg.cif.RATE_UOM):
                        CnsSpendAmount = aUnitRate * (drMetrics.CNSMP_TOTAL_UNITS || 0)
                        ShpSpendAmount = aUnitRate * (drMetrics.SHIPMENT_UNITS || 0)
                        OvrdSpendAmount = aUnitRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_ListSales, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate / 100 * (drMetrics.CNSMP_TOTAL_LISTSLS || 0)
                        ShpSpendAmount = aRate / 100 * (drMetrics.SHIPMENT_LISTSLS || 0)
                        OvrdSpendAmount = aRate / 100 * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD * drMetrics.LIST_PRICE)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Ordered, espg.cif.RATE_UOM):
                        CnsSpendAmount = 0 // Unknown, 0 ?
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_ORDER_QTY || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_BUOM, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_STAT || 0)  // Assumes BUOM = Cs
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_BUOM_QTY || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;


                    case _.includes(TPMConstants.Spend_UOM_StatCase, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_STAT || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_STAT || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Weight, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_WT || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_WT || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;


                    default:
                        CnsSpendAmount = 0
                        ShpSpendAmount = 0
                        OvrdSpendAmount = 0
                        SpendAmount = 0
                }
                break;

            case _.includes(TPMConstants.Spend_Basis_Shipped, espg.cif.RATE_BASIS_ID):
                switch (true) {

                    case _.includes(TPMConstants.Spend_UOM_Units, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_UNITS || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_UNITS || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = ShpSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Case, espg.cif.RATE_UOM):
                        CnsSpendAmount = aUnitRate * (drMetrics.CNSMP_TOTAL_UNITS || 0)
                        ShpSpendAmount = aUnitRate * (drMetrics.SHIPMENT_UNITS || 0)
                        OvrdSpendAmount = aUnitRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = ShpSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_ListSales, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate / 100 * (drMetrics.CNSMP_TOTAL_LISTSLS || 0)
                        ShpSpendAmount = aRate / 100 * (drMetrics.SHIPMENT_LISTSLS || 0)
                        OvrdSpendAmount = aRate / 100 * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD * drMetrics.LIST_PRICE)
                        SpendAmount = ShpSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Ordered, espg.cif.RATE_UOM):
                        CnsSpendAmount = 0 // Unknown, 0 ?
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_ORDER_QTY || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = ShpSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_BUOM, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_STAT || 0)  // Assumes BUOM = Cs
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_BUOM_QTY || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = CnsSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_StatCase, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_STAT || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_STAT || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = ShpSpendAmount
                        break;

                    case _.includes(TPMConstants.Spend_UOM_Weight, espg.cif.RATE_UOM):
                        CnsSpendAmount = aRate * (drMetrics.CNSMP_TOTAL_WT || 0)
                        ShpSpendAmount = aRate * (drMetrics.SHIPMENT_WT || 0)
                        OvrdSpendAmount = aRate * (espg.WB_EventSpendPrdGrpOverrideRow.cif.LIABILITY_QTY_OVRD)
                        SpendAmount = ShpSpendAmount
                        break;

                    default:
                        CnsSpendAmount = 0
                        ShpSpendAmount = 0
                        OvrdSpendAmount = 0
                        SpendAmount = 0
                }
                break;

        }

        CnsSpendAmount = NumberUtils.round(CnsSpendAmount, 2)
        ShpSpendAmount = NumberUtils.round(ShpSpendAmount, 2)
        OvrdSpendAmount = NumberUtils.round(OvrdSpendAmount, 2)
        SpendAmount = NumberUtils.round(SpendAmount, 2)

        drMetrics.SPEND_RATEBYCNSMP = CnsSpendAmount
        drMetrics.SPEND_RATEBYSHIPMENT = ShpSpendAmount
        drMetrics.SPEND_LIABILITY_QTY_OVRD = OvrdSpendAmount

        if (bConsiderOverrides) {
            switch (ovrd.cif.SPEND_CALC_METHOD) {
                case Spend_Calc_Method_TPM_Actual:
                    SpendAmount = espg.cif.SPEND_ACTUAL_FINAL
                    break;
                case Spend_Calc_Method_TPM_LE:
                    SpendAmount = espg.cif.SPEND_TPM_LE
                    break;
                case Spend_Calc_Method_Consumption:
                    SpendAmount = CnsSpendAmount
                    break;
                case Spend_Calc_Method_Shipments:
                    SpendAmount = ShpSpendAmount
                    break;
                case Spend_Calc_Method_Override:
                    SpendAmount = OvrdSpendAmount
                    break;
                case Spend_Calc_Method_Planned:
                    SpendAmount = espg.cif.SPEND_PLANNED
                    break;
                default:
            }
        } else {
            SpendAmount = espg.cif.SPEND_ACTUAL_FINAL;
        }

        drMetrics.SPEND_TOTAL = SpendAmount
        if(bVariable)
            drMetrics.SPEND_VARIABLE = SpendAmount
        else
            drMetrics.SPEND_FIXED = SpendAmount

        let aSpendType: WB_SpendTypeRow = espg.WB_SpendTypeRow
        if (aSpendType && (aSpendType.cif.WB_SPENDM_ID == espg.cif.WB_SPENDM_ID)) {
            // Use the loaded value
        } else {
            aSpendType = evt.dbContext.get(esWB_Spend_Type, espg.cif.WB_SPENDM_ID)
        }

        if (aSpendType) {
            if (aSpendType.cif.TRADEIND == '1') {
                drMetrics.SPEND_TRADE = SpendAmount
                if(bVariable)
                    drMetrics.SPEND_VARIABLE_TRADE = SpendAmount
                else
                    drMetrics.SPEND_FIXED_TRADE = SpendAmount
            }

            if (aSpendType.cif.RETAILERMARGININD == '1') {
                drMetrics.SPEND_ALLIN = SpendAmount
            }

            if (aSpendType.cif.CONSUMERDISCOUNTIND == '1') {
                drMetrics.SPEND_CONSUMERDISCOUNT = SpendAmount
            }

            if (aSpendType.cif.SHOPPERMARKETINGIND == '1') {
                drMetrics.SPEND_SHOPPER_MARKETING = SpendAmount
            }

            if (espg.cif.MANUAL_IND == '1') {
                drMetrics.SPEND_OTHER_WB = SpendAmount
            }
        }

    }

    //</editor-fold>

    //<editor-fold desc="CalculateStep_lcl_loadOverlapVolumes  Utils">

    static Event_createUnionMetricParametersSet(anEvent: EventBO): Promise<IMetricParameterSet> {
        const self = this
        return new Promise<IMetricParameterSet>((resolve, error) => {
            let returnValue: IMetricParameterSet = new MetricsParameterSet()
            let eventParameters = new MetricParameters()
            eventParameters.MetricParameterId = 'EVT-' + anEvent.WB_Event.cif.WB_EVT_ID
            eventParameters.bCalculateIntersection = false
            eventParameters.PlanningCustomers.push(anEvent.WB_Event.cif.WB_CUST_ID_PLANNING)
            eventParameters.SourceCustomers = _.map(anEvent.SourceCustomers, (aRow) => {
                return aRow.cif.WB_CUST_ID
            })
            eventParameters.InStoreStartDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.StartDt || anEvent.InStore.StartDt
            eventParameters.InStoreEndDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.EndDt || anEvent.InStore.EndDt
            eventParameters.ShipmentStartDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.StartDt || anEvent.Shipment.StartDt
            eventParameters.ShipmentEndDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.EndDt || anEvent.Shipment.EndDt

            let finishedProducts: number[] = []
            _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, (aRow: WB_EventProductGroupRow) => {
                return aRow.isActive && (!_.isEmpty(aRow.WB_ProductGroupRow))
            }), (aEPGRow: WB_EventProductGroupRow) => {
                // Get Products
                _.forEach(_.filter(aEPGRow.WB_EventProductRows, 'isActive'), (aFPRow) => {
                    finishedProducts.push(aFPRow.cif.WB_PROD_ID)
                })
            })

            eventParameters.FinishedProducts = finishedProducts
            returnValue[eventParameters.MetricParameterId] = eventParameters


            async function loadFRBOs() {
                for (let aFRRow of anEvent.WB_Event.WB_EventFinanceRelationshipRows) {
                    if (aFRRow.isActive) {

                        let eventRow: WB_EventRow = aFRRow.WB_EventRow_Target
                        let eventOverrideRow: WB_EventOverrideRow = await anEvent.dbContext.find(esWB_Event_Override, aFRRow.cif.WB_EVT_ID_REL)
                        let sourceCustomers: WB_EventCustomerRow[] = await anEvent.dbContext.findAll(esWB_Event_Customer, {where: {WB_EVT_ID: aFRRow.cif.WB_EVT_ID_REL}})
                        let ppgRows: WB_EventProductGroupRow[] = await anEvent.dbContext.findAll(esWB_Event_Product_Group, {
                            where: {
                                WB_EVT_ID: aFRRow.cif.WB_EVT_ID_REL,
                                DELETED_IND: {neq: '1'}
                            }
                        })
                        let prodRows: WB_EventProductRow[] = await anEvent.dbContext.findAll(esWB_Event_Product, {where: {WB_EVTPG_ID: {inq: _.map(ppgRows, 'WB_EVTPG_ID')}}})


                        // Event Parameters for Reference only... not added to Set
                        eventParameters = new MetricParameters()
                        eventParameters.MetricParameterId = 'EVTFR-' + aFRRow.cif.WB_EVTFR_ID
                        eventParameters.bCalculateIntersection = false
                        eventParameters.PlanningCustomers.push(eventRow.cif.WB_CUST_ID_PLANNING)
                        eventParameters.SourceCustomers = sourceCustomers.length ? _.map(sourceCustomers, 'WB_CUST_ID_SOURCE') : []
                        eventParameters.InStoreStartDt = eventOverrideRow.InStoreAligned.Range.StartDt || DateUtils.coalesce(eventOverrideRow.InStore.Range, eventRow.InStore.Range).StartDt
                        eventParameters.InStoreEndDt = eventOverrideRow.InStoreAligned.Range.EndDt || DateUtils.coalesce(eventOverrideRow.InStore.Range, eventRow.InStore.Range).EndDt
                        eventParameters.ShipmentStartDt = eventOverrideRow.ShipmentAligned.Range.StartDt || DateUtils.coalesce(eventOverrideRow.Shipment.Range, eventRow.Shipment.Range).StartDt
                        eventParameters.ShipmentEndDt = eventOverrideRow.ShipmentAligned.Range.EndDt || DateUtils.coalesce(eventOverrideRow.Shipment.Range, eventRow.Shipment.Range).EndDt
                        eventParameters.FinishedProducts = prodRows.length ? _.map(prodRows, 'WB_PROD_ID') : []
                        returnValue[eventParameters.MetricParameterId] = eventParameters
                    }
                }
            }

            loadFRBOs().then(() => {
                resolve(returnValue)
            }).catch((err => {
                error(err)
            }))

        })
    }

    static Event_applyUnionMetricResultSet(anEvent: EventBO, aMetricsSet: IMetricDetails): Promise<void> {
        let evtFRKey = 'EVT-' + anEvent.WB_Event.cif.WB_EVT_ID
        let FundingMetrics = anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_Funding
        if (_.filter(aMetricsSet.DetailRows, aMetric => aMetric.Key == evtFRKey).length && FundingMetrics) {
            MetricRowUtils.applyVolume(FundingMetrics, _.first(_.filter(aMetricsSet.DetailRows, aMetric => aMetric.Key == evtFRKey)).Metrics)
        }

        _.forEach(_.filter(anEvent.WB_Event.WB_EventFinanceRelationshipRows, 'isActive'), (aFRRow: WB_EventFRRow) => {
            evtFRKey = 'EVTFR-' + aFRRow.cif.WB_EVTFR_ID
            FundingMetrics = aFRRow.WB_MetricRow_Funding
            if (_.filter(aMetricsSet.DetailRows, aMetric => aMetric.Key == evtFRKey).length && FundingMetrics) {
                MetricRowUtils.applyVolume(FundingMetrics, _.first(_.filter(aMetricsSet.DetailRows, aMetric => aMetric.Key == evtFRKey)).Metrics)
            }
        })

        return ASync.resolve()
    }

    //</editor-fold>


    //<editor-fold desc="CalculateStep_Common_aggregateSpend  Utils">

    static Event_aggregateEventSpend(anEvent: EventBO): Promise<void> {
        // Iterate of SPG & RSPG, writing to EvtPG & Evt Summaries
        let evtAct = anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_Actual_Pending
        let evtActOvrd = anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_ActualOvrd_Pending
        let dctActByPG: { [id: number]: IwbMetric } = {}
        let dctActOvrdByPG: { [id: number]: IwbMetric } = {}


        // Zero Spend
        EventActualsCalculator.MetricRow_resetSpend(evtAct)
        EventActualsCalculator.MetricRow_resetSpend(evtActOvrd)
        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, 'isActive'), (aRow: WB_EventProductGroupRow) => {
            EventActualsCalculator.MetricRow_resetSpend(aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_Actual_Pending)
            EventActualsCalculator.MetricRow_resetSpend(aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending)

            dctActByPG[aRow.cif.WB_PRDGRP_ID] = aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_Actual_Pending
            dctActOvrdByPG[aRow.cif.WB_PRDGRP_ID] = aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending
        })

        // Aggregate
        //  SPG Records
        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_ProductGroupRows, 'isActive'), (aRow: WB_EventSpendPrdGrpRow) => {
            //
            let WB_PG_ID = aRow.cif.WB_PRDGRP_ID
            let evtPGAct = dctActByPG[WB_PG_ID]
            let evtPGActOvrd = dctActOvrdByPG[WB_PG_ID]

            //
            let srcAct = aRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_Actual_Pending
            let srcActOvrd = <IwbMetric> aRow.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_ActualOvrd_Pending

            srcAct.SPEND_PA_LE = aRow.cif.SPEND_PA_LE
            srcActOvrd.SPEND_PA_LE = aRow.cif.SPEND_PA_LE

            EventActualsCalculator.MetricRow_incrSpend(srcAct, evtAct)
            EventActualsCalculator.MetricRow_incrSpend(srcAct, evtPGAct)

            EventActualsCalculator.MetricRow_incrSpend(srcActOvrd, evtActOvrd)
            EventActualsCalculator.MetricRow_incrSpend(srcActOvrd, evtPGActOvrd)

        })

        //  SPG Records
        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_RedistributionRows, 'isActive'), (aRow: WB_EventSpendPrdGrpRedistribRow) => {
            //
            let WB_PG_ID = aRow.cif.WB_PRDGRP_ID
            let evtPGAct = dctActByPG[WB_PG_ID]
            let evtPGActOvrd = dctActOvrdByPG[WB_PG_ID]

            //
            let srcActOvrd = aRow.WB_MetricRow_Overlap

            EventActualsCalculator.MetricRow_incrSpend(srcActOvrd, evtActOvrd)
            EventActualsCalculator.MetricRow_incrSpend(srcActOvrd, evtPGActOvrd)

        })


        return ASync.resolve()
    }

    static MetricRow_resetSpend(tgt: IwbMetric) {
        tgt.SPEND_TOTAL = 0
        tgt.SPEND_FIXED = 0
        tgt.SPEND_VARIABLE = 0
        tgt.SPEND_TRADE = 0
        tgt.SPEND_FIXED_TRADE = 0
        tgt.SPEND_VARIABLE_TRADE = 0
        tgt.SPEND_ALLIN = 0
        tgt.SPEND_CONSUMERDISCOUNT = 0
        tgt.SPEND_SHOPPER_MARKETING = 0
        tgt.SPEND_OTHER_WB = 0
        tgt.SPEND_RATEBYCNSMP = 0
        tgt.SPEND_RATEBYSHIPMENT = 0
        tgt.SPEND_PA_LE = 0

        if(_.isFunction(tgt?.notify))
            tgt.notify("All")
    }

    static MetricRow_incrSpend(src: IwbMetric, tgt: IwbMetric): void {
        if (_.isEmpty(src)) {
            return
        }
        if (_.isEmpty(tgt)) {
            return
        }

        tgt.SPEND_TOTAL += src.SPEND_TOTAL || 0
        tgt.SPEND_VARIABLE += src.SPEND_VARIABLE || 0
        tgt.SPEND_FIXED += src.SPEND_FIXED || 0
        tgt.SPEND_TRADE += src.SPEND_TRADE || 0
        tgt.SPEND_VARIABLE_TRADE += src.SPEND_VARIABLE_TRADE || 0
        tgt.SPEND_FIXED_TRADE += src.SPEND_FIXED_TRADE || 0
        tgt.SPEND_ALLIN += src.SPEND_ALLIN || 0
        tgt.SPEND_CONSUMERDISCOUNT += src.SPEND_CONSUMERDISCOUNT || 0
        tgt.SPEND_SHOPPER_MARKETING += src.SPEND_SHOPPER_MARKETING || 0
        tgt.SPEND_OTHER_WB += src.SPEND_OTHER_WB || 0
        tgt.SPEND_RATEBYCNSMP += src.SPEND_RATEBYCNSMP || 0
        tgt.SPEND_RATEBYSHIPMENT += src.SPEND_RATEBYSHIPMENT || 0
        tgt.SPEND_PA_LE += src.SPEND_PA_LE || 0

        if(_.isFunction(tgt?.notify))
            tgt.notify("All")

    }

    //</editor-fold>

    //<editor-fold desc="CalculateStep_Common_calcCompoundMetric  Utils">

    static async Event_calculateEventSpendCompoundMetrics(anEvent: EventBO): Promise<void> {

        MetricRowUtils.calcSpendCompoundMetrics(anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_Actual_Pending)
        MetricRowUtils.calcSpendCompoundMetrics(anEvent.WB_Event.WB_EventOverrideRow.WB_MetricRow_ActualOvrd_Pending)

        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, 'isActive'), (aRow: WB_EventProductGroupRow) => {
            MetricRowUtils.calcSpendCompoundMetrics(aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_Actual_Pending)
            MetricRowUtils.calcSpendCompoundMetrics(aRow.WB_EventProductGroupOverrideRow.WB_MetricRow_ActualOvrd_Pending)
        })

    }


    //</editor-fold>

    //<editor-fold desc="CalculateStep_Rdst_calcDates  Utils">

    static async Event_Rdst_alignDates(anEvent: EventBO): Promise<void> {

        await EventActualsCalculator.Event_Lcl_setEffectiveDates(anEvent)

        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_RedistributionRows, 'isActive'), (aRow: WB_EventSpendPrdGrpRedistribRow) => {
            let ESPG: WB_EventSpendPrdGrpRow = aRow.WB_EventSpendPrdGrpRow
            if (!ESPG) {
                return
            }

            //  It's possible the ESPG isn't aligned ..
            let espgCons: IDateRange = {
                StartDt: ESPG.WB_EventSpendPrdGrpOverrideRow.InStoreAligned.Range.StartDt || ESPG.WB_EventSpendPrdGrpOverrideRow.InStoreStartDtOvrd || ESPG.InStoreStartDt,
                EndDt: ESPG.WB_EventSpendPrdGrpOverrideRow.InStoreAligned.Range.EndDt || ESPG.WB_EventSpendPrdGrpOverrideRow.InStoreEndDtOvrd || ESPG.InStoreEndDt
            }

            let espgShip: IDateRange = {
                StartDt: ESPG.WB_EventSpendPrdGrpOverrideRow.ShipmentAligned.Range.StartDt || ESPG.WB_EventSpendPrdGrpOverrideRow.ShipmentStartDtOvrd || ESPG.ShipmentStartDt,
                EndDt: ESPG.WB_EventSpendPrdGrpOverrideRow.ShipmentAligned.Range.EndDt || ESPG.WB_EventSpendPrdGrpOverrideRow.ShipmentEndDtOvrd || ESPG.ShipmentEndDt
            }

            let rngOvrlpCons = DateUtils.RangeIntersect(anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range, espgCons)
            let rngOvrlpShip = DateUtils.RangeIntersect(anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range, espgShip)


            aRow.InStoreOvrlp.Range = rngOvrlpCons
            aRow.ShipmentOvrlp.Range = rngOvrlpShip

            let InStoreOvrlp_Days_N = DateUtils.durationInDays(rngOvrlpCons)
            let InStoreESPG_Days_D = DateUtils.durationInDays(espgCons)
            let ShipmentOvrlp_Days_N = DateUtils.durationInDays(rngOvrlpShip)
            let ShipmentESPG_Days_D = DateUtils.durationInDays(espgShip)

            if (InStoreESPG_Days_D > 0) {
                aRow.cif.OVRLP_INSTORE_PCNT = InStoreOvrlp_Days_N / InStoreESPG_Days_D
            } else {
                aRow.cif.OVRLP_INSTORE_PCNT = 0
            }

            if (ShipmentESPG_Days_D > 0) {
                aRow.cif.OVRLP_SHIPMENT_PCNT = ShipmentOvrlp_Days_N / ShipmentESPG_Days_D
            } else {
                aRow.cif.OVRLP_SHIPMENT_PCNT = 0
            }


        })

    }

    //</editor-fold>

    //<editor-fold desc="CalculateStep_Rdst_loadOverlapVolumes  Utils">

    static async Event_createOverlapMetricParametersSet(anEvent: EventBO): Promise<MetricsParameterSet> {
        const self = this
        let returnValue = new MetricsParameterSet()

        // Event Parameters for Reference only... not added to Set
        let eventParameters = new MetricParameters()
        eventParameters.MetricParameterId = 'EVT-' + anEvent.WB_Event.cif.WB_EVT_ID
        eventParameters.bCalculateIntersection = false
        eventParameters.PlanningCustomers.push(anEvent.WB_Event.cif.WB_CUST_ID_PLANNING)
        eventParameters.SourceCustomers = _.map(anEvent.SourceCustomers, (aRow) => {
            return aRow.cif.WB_CUST_ID
        })
        eventParameters.InStoreStartDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.StartDt || anEvent.InStore.StartDt
        eventParameters.InStoreEndDt = anEvent.WB_Event.WB_EventOverrideRow.InStoreAligned.Range.EndDt || anEvent.InStore.EndDt
        eventParameters.ShipmentStartDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.StartDt || anEvent.Shipment.StartDt
        eventParameters.ShipmentEndDt = anEvent.WB_Event.WB_EventOverrideRow.ShipmentAligned.Range.EndDt || anEvent.Shipment.EndDt

        let dctFPsByPG: { [pg_id: number]: number[] } = {}

        function pushPGFP(pg_id: number, fp_id: number) {

            if (!(pg_id in dctFPsByPG)) {
                dctFPsByPG[pg_id] = [fp_id]
            } else {
                dctFPsByPG[pg_id].push(fp_id)
            }
        }

        _.forEach(_.filter(anEvent.WB_Event.WB_Event_ProductGroupRows, (aRow: WB_EventProductGroupRow) => {
            return aRow.isActive && (!_.isEmpty(aRow.WB_ProductGroupRow))
        }), (aEPGRow: WB_EventProductGroupRow) => {
            // Get Products
            _.forEach(_.filter(aEPGRow.WB_EventProductRows, 'isActive'), (aFPRow) => {
                pushPGFP(aEPGRow.cif.WB_PRDGRP_ID, aFPRow.cif.WB_PROD_ID)
            })

        })


        let dctEvtFPsByPG = dctFPsByPG
        dctFPsByPG = {}

        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_RedistributionRows, (aRow: WB_EventSpendPrdGrpRedistribRow) => {
                return aRow.isActive && aRow.WB_ProductGroupRow
            }),
            (aRSPGRow: WB_EventSpendPrdGrpRedistribRow) => {
                //
                let aSPGRow = aRSPGRow.WB_EventSpendPrdGrpRow

                if (_.isEmpty(aSPGRow))
                    return


                _.forEach(_.filter(aSPGRow.WB_EventSpendPrdRows, 'isActive'), (aFPRow) => {
                    pushPGFP(aSPGRow.cif.WB_EVTSPG_ID, aFPRow.cif.WB_PROD_ID)
                })

                let eventRSPGParameters = new MetricParameters()
                eventRSPGParameters.MetricParameterId = 'EVTRSPG-' + aRSPGRow.cif.WB_EVTRSPG_ID
                eventRSPGParameters.bCalculateIntersection = false
                eventRSPGParameters.PlanningCustomers = eventParameters.PlanningCustomers
                // TODO: Update, Use Tactic/ ESPG Source Customers.. when present
                eventRSPGParameters.SourceCustomers = eventParameters.SourceCustomers
                eventRSPGParameters.InStoreStartDt = aRSPGRow.InStoreOvrlp.Range.StartDt
                eventRSPGParameters.InStoreEndDt = aRSPGRow.InStoreOvrlp.Range.EndDt
                eventRSPGParameters.ShipmentStartDt = aRSPGRow.ShipmentOvrlp.Range.StartDt
                eventRSPGParameters.ShipmentEndDt = aRSPGRow.ShipmentOvrlp.Range.EndDt
                // Intersect
                let EvtPG_FPs = dctEvtFPsByPG[aSPGRow.cif.WB_PRDGRP_ID] || []
                let EvtSPG_FPs = dctFPsByPG[aSPGRow.cif.WB_EVTSPG_ID] || []

                if (EvtSPG_FPs.length == 0) {
                    eventRSPGParameters.FinishedProducts = EvtPG_FPs
                } else {
                    eventRSPGParameters.FinishedProducts = _.intersection(EvtPG_FPs, EvtSPG_FPs)
                }
                returnValue[eventRSPGParameters.MetricParameterId] = eventRSPGParameters
            })

        return returnValue
    }

    static async Event_applyOverlapMetricResultSet(anEvent: EventBO, aMetricsSet: IMetricsSet): Promise<void> {


        _.forEach(_.filter(anEvent.WB_Event.WB_EventSpend_RedistributionRows, 'isActive'), (aERSPGRow: WB_EventSpendPrdGrpRedistribRow) => {

            let evtrspgKey = 'EVTRSPG-' + aERSPGRow.cif.WB_EVTRSPG_ID

            if (evtrspgKey in aMetricsSet) {
                //
                let OverlapMetrics = aERSPGRow.WB_MetricRow_Overlap

                if (OverlapMetrics) {
                    MetricRowUtils.applyVolume(OverlapMetrics, aMetricsSet[evtrspgKey])
                }
            }
        })

    }

    //</editor-fold>

    //<editor-fold desc="CalculateStep_Rdst_calcSpend  Utils">

    static async Event_calculateEventRedistribSpend(anEvent: EventBO) {


        let arrERSPG = _.filter(anEvent.WB_Event.WB_EventSpend_RedistributionRows, 'isActive')
        for (var idx = 0; idx < arrERSPG.length; idx++) {
            //
            //
            // Calculate Actual Ovrlp
            let anERSPGRow = arrERSPG[idx]
            await EventActualsCalculator.EventRSPG_calculate(anEvent, anERSPGRow)

        }

    }

    static async EventRSPG_calculate(evt: EventBO, erspg: WB_EventSpendPrdGrpRedistribRow) {

        // Reset Spend
        let SpendAmount = 0
        let ESPGSpendAmount = 0
        let SpendAmountCnsOverlap = 0
        let SpendAmountShpOverlap = 0
        let drMetricsOvrlp = erspg.WB_MetricRow_Overlap
        let espg = erspg.WB_EventSpendPrdGrpRow
        let bVariable = !_.includes(TPMConstants.Spend_Basis_Fixed, espg.cif.RATE_BASIS_ID)

        let drMetricsESPG = undefined
        if (espg && espg.WB_EventSpendPrdGrpOverrideRow) {
            drMetricsESPG = espg.WB_EventSpendPrdGrpOverrideRow.WB_MetricRow_ActualOvrd
            if (drMetricsESPG) {
                ESPGSpendAmount = NumberUtils.round(espg.cif.SPEND_PA_LE, 2)
            }
        }

        // Calculate Overlap Spend
        if (!_.isEmpty(drMetricsOvrlp)) {

            EventActualsCalculator.MetricRow_resetSpend(erspg.WB_MetricRow_Overlap)

            // Get Unit Rate
            // TODO: Revise
            let UnitRate = 0
            if (espg && (!_.includes(Spend_Basis_Fixed, espg.cif.RATE_BASIS_ID))) {
                UnitRate = espg.cif.RATE_AMT_PER_UNIT
            }
            SpendAmountCnsOverlap = NumberUtils.round(UnitRate * drMetricsOvrlp.cif.CNSMP_TOTAL_UNITS, 2)
            SpendAmountShpOverlap = NumberUtils.round(UnitRate * drMetricsOvrlp.cif.SHIPMENT_UNITS, 2)

            erspg.cif.OVRLP_SPEND_CNS = SpendAmountCnsOverlap
            erspg.cif.OVRLP_SPEND_SHIP = SpendAmountShpOverlap
        }

        erspg.cif.OVRLP_SPEND_CALC_DT = DateUtils.convertToSAPBW(DateUtils.today)

        // Check the Basis
        let RedistBasis: WB_DomainItemRow = evt.dbContext.get(esWB_Domain_Item, erspg.cif.BASIS_ID)
        if (_.isEmpty(RedistBasis)) {
            let arrRedistBasis = await evt.dbContext.findUsingCache(esWB_Domain_Item, [erspg.cif.BASIS_ID])
            if (arrRedistBasis.length) {
                RedistBasis = <any>arrRedistBasis[0]
            }
        }
        let RedistBasisId = ''
        if (RedistBasis) {
            RedistBasisId = RedistBasis.cif.DOMAIN_ITEM_ID
        }

        let bBoundSpendAmount = true
        switch (RedistBasisId) {
            case Spend_Redistrib_Basis_Fixed:
                SpendAmount = erspg.cif.BASIS_RATE_AMT
                break;

            case Spend_Redistrib_Basis_Full:
                SpendAmount = ESPGSpendAmount
                break;

            case Spend_Redistrib_Basis_Pcnt:
                SpendAmount = NumberUtils.round(ESPGSpendAmount * erspg.cif.BASIS_RATE_AMT / 100, 2)
                break;

            // case Spend_Redistrib_Basis_ConsOvrlp:
            //     bBoundSpendAmount = false
            //     SpendAmount = SpendAmountCnsOverlap
            //     break;
            //
            // case Spend_Redistrib_Basis_ShipOvrlp:
            //     bBoundSpendAmount = false
            //     SpendAmount = SpendAmountShpOverlap
            //     break;

            default:
                SpendAmount = erspg.cif.BASIS_RATE_AMT  // Min against
                break;
        }

        if (bBoundSpendAmount) {
            SpendAmount = Math.min(Math.max(0, SpendAmount), ESPGSpendAmount)
        }

        erspg.cif.ALLOCATED_SPEND = SpendAmount


        if (!_.isEmpty(drMetricsOvrlp)) {
            drMetricsOvrlp.cif.SPEND_TOTAL = SpendAmount
            if(bVariable)
                drMetricsOvrlp.cif.SPEND_VARIABLE = SpendAmount
            else
                drMetricsOvrlp.cif.SPEND_FIXED = SpendAmount

            if (!espg)
                return

            let aSpendType: WB_SpendTypeRow = espg.WB_SpendTypeRow
            if (aSpendType && (aSpendType.cif.WB_SPENDM_ID == espg.cif.WB_SPENDM_ID)) {
                // Use the loaded value
            } else {
                aSpendType = evt.dbContext.get(esWB_Spend_Type, espg.cif.WB_SPENDM_ID)
            }


            if (aSpendType) {
                if (aSpendType.cif.TRADEIND == '1') {
                    drMetricsOvrlp.cif.SPEND_TRADE = SpendAmount
                    if(bVariable)
                        drMetricsOvrlp.cif.SPEND_VARIABLE_TRADE = SpendAmount
                    else
                        drMetricsOvrlp.cif.SPEND_FIXED_TRADE = SpendAmount

                }

                if (aSpendType.cif.RETAILERMARGININD == '1') {
                    drMetricsOvrlp.cif.SPEND_ALLIN = SpendAmount
                }

                if (aSpendType.cif.CONSUMERDISCOUNTIND == '1') {
                    drMetricsOvrlp.cif.SPEND_CONSUMERDISCOUNT = SpendAmount
                }

                if (aSpendType.cif.SHOPPERMARKETINGIND == '1') {
                    drMetricsOvrlp.cif.SPEND_SHOPPER_MARKETING = SpendAmount
                }

                if (espg.cif.MANUAL_IND == '1') {
                    drMetricsOvrlp.cif.SPEND_OTHER_WB = SpendAmount
                }
            }
        }



    }


    //</editor-fold>


    //</editor-fold>

}